import { RDKitModule } from "@rdkit/rdkit";
import { customInstance } from "../api/custom-instance";
import { DataFile, Dataset, Model, ModelFamily, Prediction, Protocol, ReadAcross, Resultset } from "../api/models";
import { TableData, TableDataField, TableHeader } from "../components/Table/DataTable"
import { classificationHeaders, regressionHeaders } from "../routes/Models/pages/IndividualModelFamily";
import { ResultsetEvaluationMetadata, ResultsetEvaluationMetadataWithStatistics, ResultsetGrouping } from "../routes/Resultsets/pages/IndividualResultset";
import { convertNumberToDecimal, formatBytes, formatDateString, model_overlay_data, splitLabel, truth_table_data } from "./formattingFns";
import { classificationMethods } from "./supplementalData";
import { ReadAcrossDataToxOnly, ReadAcrossDataType } from "../routes/ReadAcross/page/IndividualReadAcrossPage";
import { IndividualAirflowJob } from "../routes/Job Queue/pages/IndividualJob";
import { ResultsetVisualizationType } from "../routes/Resultsets/components/ResultsetVisualizations";
import { ResultsetVisualizationDownloadFormat } from "../hooks/endpoints/useDownload";

export interface MetadataAssociatedFile {
    type: "dataset" | "model" | "resultset" | "readacross" | "file",
    name: string,
    hash: string,
    size?: string,
}

export interface MetadataGroupInfo {
    param: string,
    groupMetadata: MetadataInfo[],
}

export interface MetadataStage {
    function_name: string,
    function_params: MetadataInfo[],
}

export interface MetadataInfo {
    label: string,
    value: string | number | string[] | MetadataAssociatedFile[] | { [key: string]: any } | boolean,
    type: 'text' | 'textarea' | 'user' | 'tags' | 'array' | 'boolean' | 'associated_files' | 'json' | 'state', 
    editableParam?: string,
    colspan: number,
}

export interface MetadataMenuItemType {
    label: string,
    metadata: MetadataInfo[] | MetadataStage[] | MetadataGroupInfo[] | (MetadataInfo | MetadataGroupInfo)[],
}

export interface ResultsetDataframeFormattingOptions {
    moleculeHighlightingMethodFilter?: string,
    includeFeatureImportanceMethodInHighlightingLabel?: boolean,
}


function isPlainObject(item: any) {
  return typeof item === 'object' && item !== null && !Array.isArray(item);
}

export type ModelTaskType = 'classification' | 'regression' | 'dcm';

export const getModelTaskType = (model: Model): ModelTaskType => {
    const modelMetadata = (model.model_metadata ?? {}) as any;

    if (model.is_dcm === true || modelMetadata.dcm_metadata) {
        return 'dcm';
    }

    const nestedMethod = modelMetadata.nested_cv_metadata?.job_metadata?.cv_model?.ml_base_cls?.name;
    const trainingMethod = modelMetadata.training_metadata?.job_metadata?.cv_model?.ml_base_cls?.name;
    const methodName = nestedMethod ?? trainingMethod ?? model.ml_method ?? '';

    if (methodName && classificationMethods.includes(methodName)) {
        return 'classification';
    }

    return 'regression';
}

export const isDcmModel = (model: Model): boolean => getModelTaskType(model) === 'dcm';

// METADATA MENU FORMATTING FUNCTIONS

export const formatDisplayedDatafileMetadata = (
    datafile: DataFile,
) => {
    const editableParams = ['name', 'description', 'measurement_type', 'target', 'organism', 'tags'];

    const {
        created_by,
        creation_date,
        datafile_metadata,
        file_extension,
        file_metadata,
        name,
        owner,
        size,
        updated_date,
    } = datafile;

    let tags = file_metadata && file_metadata.tags ? file_metadata.tags : [];
    if (tags.length > 0) {
        tags = tags.map(tag => {
            if (tag === null) {
                return 'null'
            } else {
                return tag;
            }
        })
    };

    const {
        description,
        measurement_type,
        organism,
        records_number: records,
        target,
    } = datafile_metadata!;

    const generalMetadata = {
        name,
        records,
        size: size ? formatBytes(size) : 0,
        creation_date: creation_date ? formatDateString(creation_date) : '',
        updated_date: updated_date ? formatDateString(updated_date) : '',
        created_by,
        owner,
        file_extension,
        tags: tags as string[],
        description,
        measurement_type,
        target,
        organism,
    };

    const datafileGeneralMetadata: MetadataInfo[] = [];

    for (const [key, value] of Object.entries(generalMetadata)) {
        if (value !== undefined) {
            let metadataInfo: MetadataInfo = {
                label: splitLabel(key),
                value: value ?? '',
                type: 'text',
                editableParam: undefined,
                colspan: 1,
            };

            if (editableParams.includes(key)) {
                metadataInfo.editableParam = key;
            }

            if (['name', 'description', 'tags'].includes(key)) {
                metadataInfo.colspan = 2;
            }

            if (['created_by', 'owner'].includes(key)) {
                metadataInfo.type = 'user';
            } else if (key === 'tags') {
                metadataInfo.type = 'tags';
            }
            
            datafileGeneralMetadata.push(metadataInfo);
        }
        
    };

    const datafileMetadataMenuItems: MetadataMenuItemType[] = [
        {
            label: 'General Metadata',
            metadata: datafileGeneralMetadata,
        }
    ];

    return datafileMetadataMenuItems;

};

export const formatDisplayedDatasetMetadata = async (
    dataset: Dataset,
) => {
    const editableParams = ['name', 'tags', 'description'];
    const datasetMetadataMenuItems: MetadataMenuItemType[] = [];

    const {
        created_by,
        creation_date,
        dataset_metadata,
        file_extension,
        file_metadata,
        name,
        owner,
        parent_data_file_hash,
        parent_dataset_hash,
        size,
        updated_date
    } = dataset;

    let tags = file_metadata && file_metadata.tags ? file_metadata.tags : [];
    if (tags.length > 0) {
        tags = tags.map(tag => {
            if (tag === null) {
                return 'null'
            } else {
                return tag;
            }
        })
    };

    const {
        dataset_types,
        records_number: records,
        stages,
        description,
    } = dataset_metadata!;

    let generalMetadata = {
        name,
        records,
        size: size ? formatBytes(size) : '0',
        creation_date: creation_date ? formatDateString(creation_date) : '',
        updated_date: updated_date ? formatDateString(updated_date) : '',
        created_by,
        owner,
        file_extension,
        tags: tags as string[],
        dataset_types: dataset_types ?? [],
        parent_datafile: parent_data_file_hash ?? undefined,
        parent_dataset: parent_dataset_hash ?? undefined,
        description,
    };

    const datasetGeneralMetadata: MetadataInfo[] = [];

    for (const [key, value] of Object.entries(generalMetadata)) {
        if (value !== undefined) {
            let metadataInfo: MetadataInfo = {
                label: splitLabel(key),
                value: value ?? '',
                type: 'text',
                editableParam: undefined,
                colspan: 1,
            };

            if (editableParams.includes(key)) {
                metadataInfo.editableParam = key;
            }

            if (['name', 'tags', 'dataset_types', 'description'].includes(key)) {
                metadataInfo.colspan = 2;
            }

            if (Array.isArray(value)) {
                if (key === 'tags') {
                    metadataInfo.type = 'tags';
                } else {
                    metadataInfo.type = 'array'
                }   
            } else if (key === 'parent_datafile') {
                metadataInfo.colspan = 2;
                metadataInfo.type = 'associated_files';
                const response = await customInstance({
                    url: `data_file/?data_file_hash=${value}`,
                    method: 'GET',
                });
                const datafile = response as unknown as DataFile;
                const datafileInfo: MetadataAssociatedFile = {
                    type: 'file',
                    name: datafile.name ?? '',
                    hash: value as string,
                    size: datafile.size ? formatBytes(datafile.size) : undefined,
                };
                metadataInfo.value = [datafileInfo];
            } else if (key === 'parent_dataset') {
                metadataInfo.colspan = 2;
                metadataInfo.type = 'associated_files';
                const response = await customInstance({
                    url: `dataset/?dataset_hash=${value}&include_dataset_metadata=false&include_airflow_metadata=false`,
                    method: 'GET',
                });
                const dataset = response as unknown as Dataset;
                const datasetInfo: MetadataAssociatedFile = {
                    type: 'dataset',
                    name: dataset.name ?? '',
                    hash: value as string,
                    size: dataset.size ? formatBytes(dataset.size) : undefined,
                };
                metadataInfo.value = [datasetInfo];
            } else if (['created_by', 'owner'].includes(key)) {
                metadataInfo.type = 'user';
            };

            datasetGeneralMetadata.push(metadataInfo);
        };
    };

    datasetMetadataMenuItems.push({
        label: 'General Metadata',
        metadata: datasetGeneralMetadata,
    });

    if (stages && stages.length > 0) {
        const metadataStages: MetadataStage[] = [];

        stages.forEach(stage => {
            let metadataStage: MetadataStage = {
                function_name: stage.function ?? '',
                function_params: [],
            };

            if (stage.function_params && Object.keys(stage.function_params).length > 0) {
                const function_params: MetadataInfo[] = [];

                for (const [key, value] of Object.entries(stage.function_params)) {
                    if (key === 'value_range' && value !== null) {
                        const valueRanges = value as any[];
                        function_params.push({
                            label: 'Value Range - Lower Bound',
                            value: valueRanges[0] ?? 'null',
                            type: 'text',
                            colspan: 1,
                        });
                        function_params.push({
                            label: 'Value Range - Upper Bound',
                            value: valueRanges[1] ?? 'null',
                            type: 'text',
                            colspan: 1,
                        });
                    } else {
                        let stageInfo: MetadataInfo = {
                            label: splitLabel(key),
                            value: value ?? 'null',
                            type: 'text',
                            colspan: 1,
                        }

                        if (typeof value === 'boolean') {
                            stageInfo.type = 'boolean';
                        } else if (key.includes('kwargs')) {
                            stageInfo.type = 'json';
                            stageInfo.colspan = 2;
                        };

                        function_params.push(stageInfo);
                    }
                };

                metadataStage.function_params = function_params;
            };

            metadataStages.push(metadataStage);
        });

        datasetMetadataMenuItems.push({
            label: 'Stages',
            metadata: metadataStages,
        });
    }

    return datasetMetadataMenuItems;

}

export const formatDisplayedModelFamilyMetadata = async (
    modelFamily: ModelFamily,
    models: Model[],
) => {

    const modelFamilyMetadata: MetadataInfo[] = [];

    const {
        created_by,
        creation_date,
        name,
        owner,
        updated_date,
        model_family_metadata,
    } = modelFamily;

    const tags: string[] = model_family_metadata && model_family_metadata.tags ? Array.from(model_family_metadata.tags).map(tag => tag === null ? 'null' : tag as string) : [];

    const generalMetadata = {
        name,
        creation_date: creation_date ? formatDateString(creation_date) : '',
        updated_date: updated_date ? formatDateString(updated_date) : '',
        created_by,
        owner,
        tags,
    };

    let datasetHashes: string[] = [];

    if (models.length > 0) {
        models.forEach(model => {
            if (model.dataset_hash) {
                datasetHashes.push(model.dataset_hash);
            }
        })
    };

    datasetHashes = Array.from(new Set(datasetHashes));

    const training_datasets: MetadataAssociatedFile[] = [];

    if (datasetHashes.length > 0) {
        const promises = datasetHashes.map(async (hash) => {
            const response = await customInstance({
                url: `dataset/?dataset_hash=${hash}&include_dataset_metadata=false&include_airflow_metadata=false`,
                method: 'GET',
            });
            const dataset = response as unknown as Dataset;
            const datasetInfo: MetadataAssociatedFile = {
                type: 'dataset',
                name: dataset.name ?? '',
                hash: hash as string,
                size: dataset.size ? formatBytes(dataset.size) : undefined,
            };
            return training_datasets.push(datasetInfo);
        });

        try {
            await Promise.all(promises);
        } catch (err:any) {
            console.log(err);
        }
    }

    for (const [key, value] of Object.entries(generalMetadata)) {
        if (value !== undefined) {
            let metadataInfo: MetadataInfo = {
                label: splitLabel(key),
                value: value ?? '',
                type: 'text',
                colspan: 1,
            };

            if (key === 'tags') {
                metadataInfo.type = 'tags';
                metadataInfo.colspan = 2;
            } else if (['created_by', 'owner'].includes(key)) {
                metadataInfo.type = 'user';
            } else if (key === 'name') {
                metadataInfo.colspan = 2;
            }

            modelFamilyMetadata.push(metadataInfo);
        }
    };

    if (training_datasets.length > 0) {
        modelFamilyMetadata.push({
            label: 'Training Datasets',
            value: training_datasets,
            type: 'associated_files',
            colspan: 2,
        })
    };
    
    const modelFamilyMetadataMenuItem: MetadataMenuItemType = {
        label: 'General Metadata',
        metadata: modelFamilyMetadata,
    };

    return [modelFamilyMetadataMenuItem];
}

export const formatDisplayedModelMetadata = async (
    modelFamilyName: string,
    model: Model
) => {
    const editableParams = ['name', 'tags'];
    const modelMetadataMenuItems: MetadataMenuItemType[] = [];

    const { 
        name,
        created_by,
        creation_date,
        file_extension,
        file_metadata,
        legacy,
        model_metadata,
        size,
        updated_date,
        owner,
        dataset_hash,
    } = model;

    const {
        fingerprint_metadata,
        training_metadata,
        nested_cv_metadata
    } = model_metadata!;

    let tags = file_metadata && file_metadata.tags ? file_metadata.tags : [];
    if (tags.length > 0) {
        tags = tags.map(tag => {
            if (tag === null) {
                return 'null'
            } else {
                return tag;
            }
        })
    };

    const generalMetadata = {
        model_family_name: modelFamilyName,
        name,
        size: size ? formatBytes(size) : '0',
        legacy,
        creation_date: creation_date ? formatDateString(creation_date) : '',
        updated_date: updated_date ? formatDateString(updated_date) : '',
        created_by,
        owner,
        file_extension,
        tags: tags as string[],
    };

    const modelGeneralMetadata: MetadataInfo[] = [];

    for (const [key, value] of Object.entries(generalMetadata)) {
        if (value !== undefined) {
            let metadataInfo: MetadataInfo = {
                label: splitLabel(key),
                value: value ?? '',
                type: 'text',
                editableParam: undefined,
                colspan: 1,
            };

            if (editableParams.includes(key)) {
                metadataInfo.editableParam = key;
            }

            if (['name', 'tags', 'model_family_name'].includes(key)) {
                metadataInfo.colspan = 2;
            }

            if (typeof value === 'boolean') {
                metadataInfo.type = 'boolean';
            } else if (key === 'tags') {
                metadataInfo.type = 'tags';
            } else if (['created_by', 'owner'].includes(key)) {
                metadataInfo.type = 'user';
            }

            modelGeneralMetadata.push(metadataInfo);
        };
    };

    if (dataset_hash) {
        const response = await customInstance({
            url: `dataset/?dataset_hash=${dataset_hash}&include_dataset_metadata=false&include_airflow_metadata=false`,
            method: 'GET',
        });
        const dataset = response as unknown as Dataset;
        const datasetInfo: MetadataAssociatedFile = {
            type: 'dataset',
            name: dataset.name ?? '',
            hash: dataset_hash as string,
            size: dataset.size ? formatBytes(dataset.size) : undefined,
        };
        modelGeneralMetadata.push({
            label: 'Training Dataset',
            value: [datasetInfo],
            type: 'associated_files',
            colspan: 2,
        });
    }

    modelMetadataMenuItems.push({
        label: 'General Metadata',
        metadata: modelGeneralMetadata,
    });

    if (fingerprint_metadata) {
        const modelFingerprintMetadata: MetadataInfo[] = [];

        const {
            job_metadata,
            runtime_environment,
        } = fingerprint_metadata;
        
        let fingerprintMetadata: any = {};

        if (job_metadata) {
            const {
                calculate_structure_analysis,
                dataset,
                fingerprint_function_pooling_type,
                fingerprint_functions,
                output_filepath,
                output_fingerprint_column_name,
                output_structure_analysis_column_name,
            } = job_metadata;

            const fingerprintJobMetadata = {
                calculate_structure_analysis,
                fingerprint_function_pooling_type,
                output_filepath,
                output_fingerprint_column_name,
                output_structure_analysis_column_name,
            }

            if (fingerprint_functions && fingerprint_functions.length > 0) {
                const descriptors: string[] = [];
                let fingerprintParams: any = {};
                fingerprint_functions.forEach(func => {
                    if (func.name) {
                        descriptors.push(func.name)
                    };
                });
                
                if (descriptors.length > 0) {
                    descriptors.forEach(descriptor => {
                        const fingerprintFunction = fingerprint_functions.find(f => f.name && f.name === descriptor);
                        if (fingerprintFunction) {
                            for (const [key, value] of Object.entries(fingerprintFunction)) {
                                if (!['KIND', 'name'].includes(key) && value !== null) {
                                    fingerprintParams[`${descriptor} ${splitLabel(key)}`] = value;
                                }
                            }
                        }
                     });

                     fingerprintMetadata['descriptors'] = descriptors;
                };

                if (Object.keys(fingerprintParams).length > 0) {
                    for (const [key, value] of Object.entries(fingerprintParams)) {
                        fingerprintMetadata[key] = value;
                    }
                }

            };

            if (dataset) {
                for (const [key, value] of Object.entries(dataset)) {
                    fingerprintMetadata[`Dataset ${splitLabel(key)}`] = value ?? 'null';
                }
            };

            for (const [key, value] of Object.entries(fingerprintJobMetadata)) {
                if (value !== undefined) {
                    fingerprintMetadata[key] = value ?? '';
                }
            };
        };

        if (runtime_environment) {
            fingerprintMetadata['runtime_environment'] = runtime_environment;
        }

        for (const [key, value] of Object.entries(fingerprintMetadata)) {
            if (value !== undefined) {
                let metadataInfo: MetadataInfo = {
                    label: splitLabel(key),
                    value: value ?? '',
                    type: 'text',
                    editableParam: undefined,
                    colspan: 1,
                };

                if (['descriptors', 'runtime_environment'].includes(key)) {
                    metadataInfo.colspan = 2;
                }

                if (key === 'runtime_environment') {
                    metadataInfo.type = 'json';
                } else if (key === 'descriptors') {
                    metadataInfo.type = 'array';
                } else if (typeof value === 'boolean') {
                    metadataInfo.type = 'boolean';
                }

                modelFingerprintMetadata.push(metadataInfo);
            };
            
        };
        
        modelMetadataMenuItems.push({
            label: 'Fingerprint Metadata',
            metadata: modelFingerprintMetadata,
        });
    };

    if (training_metadata) {
        const modelTrainingMetadata: (MetadataInfo | MetadataGroupInfo)[] = [];

        const {
            job_metadata,
            ml_model_params,
            runtime_environment,
        } = training_metadata;

        const trainingMetadata = {
            ml_model_params,
            runtime_environment
        };

        if (job_metadata) {
            const {
                cv_model,
                featureset,
                keep_additional_holdout_set,
                holdout_evaluation_functions,
                holdout_splitter,
            } = job_metadata;

            const trainingJobMetadata = {
                cv_model,
                featureset,
                keep_additional_holdout_set,
                holdout_evaluation_functions,
                holdout_splitter,
            }

            for (const [groupKey, groupValue] of Object.entries(trainingJobMetadata)) {
                if (groupValue !== undefined) {
                    if (!isPlainObject(groupValue)) {
                        modelTrainingMetadata.push({
                            label: splitLabel(groupKey),
                            value: groupValue ?? 'null',
                            type: typeof groupValue === 'boolean' ? 'boolean' : 'text',
                            colspan: 1,
                        });
                    } else {
                        if ((groupKey.includes('holdout') && trainingJobMetadata.keep_additional_holdout_set) || !groupKey.includes('holdout')) {
                            const metadataGroupInfo: MetadataInfo[] = [];

                            for (const [key, value] of Object.entries(groupValue as any)) {
                                let metadataInfo: MetadataInfo = {
                                    label: key,
                                    value: value ?? 'null',
                                    type: 'text',
                                    colspan: 1,
                                };

                                if (isPlainObject(value)) {
                                    metadataInfo.type = 'json';
                                    metadataInfo.colspan = 2;
                                }

                                metadataGroupInfo.push(metadataInfo);
                            };

                            modelTrainingMetadata.push({
                                param: groupKey,
                                groupMetadata: metadataGroupInfo,
                            });
                        }
                    }
                }
            }
        };

        for (const [key, value] of Object.entries(trainingMetadata)) {
            if (value !== undefined) {
                if (key === 'runtime_environment') {
                    modelTrainingMetadata.push({
                        label: splitLabel(key),
                        value: value ?? 'null',
                        type: 'json',
                        colspan: 2,
                    });
                } else if (isPlainObject(value)) {
                    const metadataGroupInfo: MetadataInfo[] = [];
                    for (const [metadataKey, metadataValue] of Object.entries(value as any)) {
                        if (metadataValue !== undefined) {
                            metadataGroupInfo.push({
                                label: splitLabel(metadataKey),
                                value: metadataValue ?? 'null',
                                type: isPlainObject(metadataValue) ? 'json' : typeof metadataValue === 'boolean' ? 'boolean' : 'text',
                                colspan: isPlainObject(metadataValue) ? 2 : 1,
                            });
                        }
                    };

                    modelTrainingMetadata.push({
                        param: key,
                        groupMetadata: metadataGroupInfo,
                    })
                };
            }
        };

        modelMetadataMenuItems.push({
            label: 'Training Metadata',
            metadata: modelTrainingMetadata,
        });
    }

    if (nested_cv_metadata) {
        const modelNestedCVMetadata: (MetadataInfo | MetadataGroupInfo)[] = [];

        const {
            job_metadata,
            output_aggregate_metrics,
            output_fold_metrics,
            runtime_environment,
        } = nested_cv_metadata;

        let nestedCVMetadata: any = {
            output_aggregate_metrics,
            output_fold_metrics,
            runtime_environment,
        };

        if (job_metadata) {
            const {
                aggregation_functions,
                cv_model,
                evaluation_cv_splits,
                evaluation_functions,
                featureset,
            } = job_metadata;

            const groupedMetadata = {
                cv_model,
                evaluation_cv_splits,
                featureset,
            };

            const ungroupedMetadata = {
                aggregation_functions,
                evaluation_functions,
            }

            for (const [groupKey, groupValue] of Object.entries(groupedMetadata)) {
                if (groupValue !== undefined) {
                    const metadataGroupInfo: MetadataInfo[] = [];

                    for (const [key, value] of Object.entries(groupValue as any)) {
                        let metadataInfo: MetadataInfo = {
                            label: key,
                            value: value ?? 'null',
                            type: 'text',
                            colspan: 1,
                        };

                        if (isPlainObject(value)) {
                            metadataInfo.type = 'json';
                            metadataInfo.colspan = 2;
                        }

                        metadataGroupInfo.push(metadataInfo);
                    };

                    modelNestedCVMetadata.push({
                        param: groupKey,
                        groupMetadata: metadataGroupInfo,
                    });
                }
            };

            for (const [groupKey, groupValue] of Object.entries(ungroupedMetadata)) {
                if (groupValue !== undefined) {
                    if ((groupValue as any).statistics.KIND) {
                        modelNestedCVMetadata.push({
                            label: `${splitLabel(groupKey)} Statistics`,
                            value: (groupValue as any).statistics.KIND ?? 'null',
                            type: 'text',
                            colspan: 1,
                        })
                    }
                }
            };
        };

        for (const [key, value] of Object.entries(nestedCVMetadata)) {
            if (value !== undefined) {
                let metadataInfo: MetadataInfo = {
                    label: splitLabel(key),
                    value: value ?? '',
                    type: 'json',
                    colspan: 2,
                };

                modelNestedCVMetadata.push(metadataInfo);
            };
        };

        modelMetadataMenuItems.push({
            label: 'Nested CV Metadata',
            metadata: modelNestedCVMetadata,
        });
    }

    return modelMetadataMenuItems;

}


export const formatDisplayedResultsetMetadata = async (
    resultset: Resultset,
    predictions: Prediction[],
    modelFamilyFiles: { id: number, name: string }[],
) => {
    const resultsetMetadata: MetadataInfo[] = [];

    const {
        created_by,
        creation_date,
        name,
        owner,
        resultset_metadata,
        updated_date,
    } = resultset;

    const {
        model_family_ids,
        smiles,
        structure_column,
    } = resultset_metadata!;

    const tags: string[] = resultset_metadata && resultset_metadata.tags ? Array.from(resultset_metadata.tags).map(tag => tag === null ? 'null' : tag as string) : [];

    const generalMetadata = {
        name,
        creation_date: creation_date ? formatDateString(creation_date) : '',
        updated_date: updated_date ? formatDateString(updated_date) : '',
        created_by,
        owner,
        tags,
        smiles: smiles ?? undefined,
        structure_column,
    };
    

    for (const [key, value] of Object.entries(generalMetadata)) {
        if (value !== undefined) {
            let metadataInfo: MetadataInfo = {
                label: splitLabel(key),
                value: value ?? '',
                type: 'text',
                colspan: 1,
            };

            if (['created_by', 'owner'].includes(key)) {
                metadataInfo.type = 'user';
            } else if (key === 'tags') {
                metadataInfo.type = 'tags';
                metadataInfo.colspan = 2;
            } else if (['name', 'smiles'].includes(key)) {
                metadataInfo.colspan = 2;
            };

            resultsetMetadata.push(metadataInfo);
        };
    };

    if (predictions.length > 0) {
        let datasetHashes: string[] = [];
        predictions.forEach(prediction => {
            if (prediction.dataset_hash) {
                datasetHashes.push(prediction.dataset_hash as string);
            };
        });

        datasetHashes = Array.from(new Set(datasetHashes));
        const test_datasets: MetadataAssociatedFile[] = [];

        if (datasetHashes.length > 0) {
            const promises = datasetHashes.map(async (hash) => {
                const response = await customInstance({
                    url: `dataset/?dataset_hash=${hash}&include_dataset_metadata=false&include_airflow_metadata=false`,
                    method: 'GET',
                });
                const dataset = response as unknown as Dataset;
                const datasetInfo: MetadataAssociatedFile = {
                    type: 'dataset',
                    name: dataset.name ?? '',
                    hash: hash as string,
                    size: dataset.size ? formatBytes(dataset.size) : undefined,
                };
                return test_datasets.push(datasetInfo);
            });

            try {
                await Promise.all(promises);
            } catch (err:any) {
                console.log(err);
            }
        };

        if (test_datasets.length > 0) {
            let metadataInfo: MetadataInfo = {
                label: 'Test Datasets',
                value: test_datasets,
                type: 'associated_files',
                colspan: 2,
            };

            resultsetMetadata.push(metadataInfo);
        }
    }

    if (model_family_ids) {
        const modelFamilyIds = Object.keys(model_family_ids);
        const modelFamilies: MetadataAssociatedFile[] = [];
        if (modelFamilyIds.length > 0) {
            const promises = modelFamilyIds.map(async (id) => {
                const response = await customInstance({
                    url: `model_family/?model_family_id=${id}&include_model_family_metadata=false&include_model_stats=false&include_airflow_metadata=false`,
                    method: 'GET',
                });
                const modelFamily = response as unknown as ModelFamily;
                const modelFamilyInfo: MetadataAssociatedFile = {
                    type: 'model',
                    name: modelFamily.name ?? '',
                    hash: id as string,
                };
                return modelFamilies.push(modelFamilyInfo);
            });

            try {
                await Promise.all(promises);
            } catch (err:any) {
                console.log(err);
            }
        };

        if (modelFamilies.length > 0) {
            resultsetMetadata.push({
                label: 'Model Families',
                value: modelFamilies,
                type: 'associated_files',
                colspan: 2,
            })
        }
    };
    
    const resultsetMetadataMenuItem: MetadataMenuItemType = {
        label: 'General Metadata',
        metadata: resultsetMetadata,
    };

    return [resultsetMetadataMenuItem];
}

export const formatDisplayedPredictionMetadata = (
    prediction: Prediction,
    method_name: string,
) => {
    const predictionMetadataList: MetadataMenuItemType[] = [];
    const predictionGeneralMetadata: MetadataInfo[] = [];

    const {
        created_by,
        creation_date,
        file_metadata,
        name,
        size,
        owner,
        updated_date,
        prediction_metadata,
    } = prediction;

    const tags: string[] = file_metadata && file_metadata.tags ? Array.from(file_metadata.tags).map(tag => tag === null ? 'null' : tag as string) : [];

    const {
        prediction_type,
        runtime_metrics,
        job_metadata,
    } = prediction_metadata!;

    const generalMetadata = {
        method_name,
        name,
        size: size ? formatBytes(size) : '0',
        prediction_type,
        creation_date: creation_date ? formatDateString(creation_date) : '',
        updated_date: updated_date ? formatDateString(updated_date) : '',
        created_by,
        owner,
        tags,
    };

    for (const [key, value] of Object.entries(generalMetadata)) {
        if (value !== undefined) {
            let metadataInfo: MetadataInfo = {
                label: splitLabel(key),
                value: value ?? '',
                type: 'text',
                colspan: 1,
            };

            if (['created_by', 'owner'].includes(key)) {
                metadataInfo.type = 'user';
            } else if (key === 'tags') {
                metadataInfo.type = 'tags';
                metadataInfo.colspan = 2;
            } else if (['name', 'method_name'].includes(key)) {
                metadataInfo.colspan = 2;
            };

            predictionGeneralMetadata.push(metadataInfo);
        };
    };

    predictionMetadataList.push({
        label: 'General Metadata',
        metadata: predictionGeneralMetadata,
    });

    if (job_metadata) {
        const predictionJobMetadata: (MetadataInfo | MetadataGroupInfo)[] = [];

        const {
            experiment,
            experiment_id,
            experiment_name,
            featureset,
            ml_model_file_location,
            model,
            run_id,
        } = job_metadata;

        const jobMetadata = {
            run_id,
            featureset,
            experiment_name,
            experiment_id,
            experiment,
            ml_model_file_location,
            model,
            runtime_metrics,
        };

        for (const [key, value] of Object.entries(jobMetadata)) {
            if (value !== undefined) {
                if (key === 'featureset' && value !== null && isPlainObject(value)) {
                    const groupMetadataInfo: MetadataInfo[] = [];
                    for (const [groupKey, groupValue] of Object.entries(value)) {
                        let metadataInfo: MetadataInfo = {
                            label: groupKey,
                            value: groupValue ?? 'null',
                            type: 'text',
                            colspan: 1,
                        };

                        groupMetadataInfo.push(metadataInfo);
                    };
                    predictionJobMetadata.push({
                        param: key,
                        groupMetadata: groupMetadataInfo,
                    });
                } else {
                    let metadataInfo: MetadataInfo = {
                        label: key,
                        value: value ?? 'null',
                        type: 'text',
                        colspan: 1,
                    };

                    if (['experiment', 'model', 'runtime_metrics'].includes(key)) {
                        metadataInfo.type = 'json';
                        metadataInfo.colspan = 2;

                        // if (key === 'model' && value !== null) {
                        //     metadataInfo.value = (value as string).replace(/\s+/g, ' ')
                        // }
                    } else if (key === 'ml_model_file_location') {
                        metadataInfo.colspan = 2;
                    }

                    predictionJobMetadata.push(metadataInfo);
                };
            }
        };

        predictionMetadataList.push({
            label: 'Job Metadata',
            metadata: predictionJobMetadata,
        });
    }

    return predictionMetadataList;

}

export const formatDisplayedResultsetVisualizationMetadata = async (
    visualization: ResultsetVisualizationType,
) => {
    const visualizationMetadataMenuItems: MetadataMenuItemType[] = [];
    const visualizationGeneralMetadata: MetadataInfo[] = [];
    const visualizationFingerprintMetadata: MetadataInfo[] = [];
    
    const {
        metadata,
        test_keys,
        train_keys,
    } = visualization;

    if (metadata) {
        const {
            creation_date,
            updated_date,
            dataset_visualization_metadata,
        } = metadata;

        const generalMetadata = {
            creation_date: creation_date ? formatDateString(creation_date) : '',
            updated_date: updated_date ? formatDateString(updated_date) : '',
        }

        for (const [key, value] of Object.entries(generalMetadata)) {
            let metadataInfo: MetadataInfo = {
                label: splitLabel(key),
                value: value ?? '',
                type: 'text',
                colspan: 1,
            };

            visualizationGeneralMetadata.push(metadataInfo);
        };

        if (dataset_visualization_metadata) {
            const {
                fingerprint_metadata,
                visualization_method_metadata,
            } = dataset_visualization_metadata as any;

            if (visualization_method_metadata) {
                const {
                    method_type,
                    params,
                } = visualization_method_metadata;

                let visMethodMetadata: any = {
                    method_type,
                };

                if (params) {
                    for (const [key, value] of Object.entries(params as any)) {
                        visMethodMetadata[key] = value;
                    }
                };

                for (const [key, value] of Object.entries(visMethodMetadata)) {
                    if (value !== undefined) {
                        let metadataInfo: MetadataInfo = {
                            label: key,
                            value: value ?? 'null',
                            type: typeof value === 'boolean' ? 'boolean' : 'text',
                            colspan: 1,
                        };
                        visualizationGeneralMetadata.push(metadataInfo);
                    }
                }
            };

            if (fingerprint_metadata) {
                const {
                    job_metadata,
                    runtime_environment,
                    runtime_metrics
                } = fingerprint_metadata;

                let fingerprintMetadata: any = {};
                
                if (job_metadata) {
                    const {
                        calculate_structure_analysis,
                        dataset,
                        fingerprint_function_pooling_type,
                        fingerprint_functions,
                        output_filepath,
                        output_fingerprint_column_name,
                        output_structure_analysis_column_name,
                    } = job_metadata;

                    const fingerprintJobMetadata = {
                        calculate_structure_analysis,
                        fingerprint_function_pooling_type,
                        output_filepath,
                        output_fingerprint_column_name,
                        output_structure_analysis_column_name,
                    }

                    if (fingerprint_functions && fingerprint_functions.length > 0) {
                        const descriptors: string[] = [];
                        let fingerprintParams: any = {};
                        fingerprint_functions.forEach((func: any) => {
                            if (func.name) {
                                descriptors.push(func.name)
                            };
                        });
                        
                        if (descriptors.length > 0) {
                            descriptors.forEach(descriptor => {
                                const fingerprintFunction = fingerprint_functions.find((f:any) => f.name && f.name === descriptor);
                                if (fingerprintFunction) {
                                    for (const [key, value] of Object.entries(fingerprintFunction)) {
                                        if (!['KIND', 'name'].includes(key) && value !== null) {
                                            fingerprintParams[`${descriptor} ${splitLabel(key)}`] = value;
                                        }
                                    }
                                }
                            });

                            fingerprintMetadata['descriptors'] = descriptors;
                        };

                        if (Object.keys(fingerprintParams).length > 0) {
                            for (const [key, value] of Object.entries(fingerprintParams)) {
                                fingerprintMetadata[key] = value;
                            }
                        }

                        if (dataset) {
                            for (const [key, value] of Object.entries(dataset)) {
                                fingerprintMetadata[`Dataset ${splitLabel(key)}`] = value ?? 'null';
                            }
                        };

                        for (const [key, value] of Object.entries(fingerprintJobMetadata)) {
                            if (value !== undefined) {
                                fingerprintMetadata[key] = value ?? '';
                            }
                        };

                    };
                };

                fingerprintMetadata['runtime_environment'] = runtime_environment;
                fingerprintMetadata['runtime_metrics'] = runtime_metrics;

                for (const [key, value] of Object.entries(fingerprintMetadata)) {
                    if (value !== undefined) {
                        let metadataInfo: MetadataInfo = {
                            label: splitLabel(key),
                            value: value ?? 'null',
                            type: typeof value === 'boolean' ? 'boolean' : 'text',
                            colspan: 1,
                        };

                        if (isPlainObject(value)) {
                            metadataInfo.type = 'json';
                            metadataInfo.colspan = 2;
                        } else if (key === 'descriptors') {
                            metadataInfo.type = 'array';
                            metadataInfo.colspan = 2;
                        } 

                        visualizationFingerprintMetadata.push(metadataInfo);
                    }
                };
            }
        }   
    };

    if (test_keys && test_keys.length > 0) {
        const test_datasets: MetadataAssociatedFile[] = [];
        const promises = test_keys.map(async (hash) => {
            const response = await customInstance({
                url: `dataset/?dataset_hash=${hash}&include_dataset_metadata=false&include_airflow_metadata=false`,
                method: 'GET',
            });
            const dataset = response as unknown as Dataset;
            const datasetInfo: MetadataAssociatedFile = {
                type: 'dataset',
                name: dataset.name ?? '',
                hash: hash as string,
                size: dataset.size ? formatBytes(dataset.size) : undefined,
            };
            return test_datasets.push(datasetInfo);
        });

        try {
            await Promise.all(promises);
            if (test_datasets.length > 0) {
                visualizationGeneralMetadata.push({
                    label: 'Test Sets',
                    value: test_datasets,
                    type: 'associated_files',
                    colspan: 2,
                })
            }
        } catch (err:any) {
            console.log(err);
        }
    }

    if (train_keys && train_keys.length > 0) {
        const train_models: MetadataAssociatedFile[] = [];
        const promises = train_keys.map(async(id) => {
            const response = await customInstance({
                url: `model_family/?model_family_id=${id}&include_model_family_metadata=false&include_model_stats=false&include_airflow_metadata=false`,
                method: 'GET',
            });
            const modelFamily = response as unknown as ModelFamily;
            const modelFamilyInfo: MetadataAssociatedFile = {
                type: 'model',
                name: modelFamily.name ?? '',
                hash: id as string,
            };
            return train_models.push(modelFamilyInfo);
        });

        try {
            await Promise.all(promises);
            if (train_models.length > 0) {
                visualizationGeneralMetadata.push({
                    label: 'Train Sets',
                    value: train_models,
                    type: 'associated_files',
                    colspan: 2,
                })
            }
        } catch (err:any) {
            console.log(err);
        }
    };

    visualizationMetadataMenuItems.push({
        label: 'General Metadata',
        metadata: visualizationGeneralMetadata,
    });

    if (visualizationFingerprintMetadata.length > 0) {
        visualizationMetadataMenuItems.push({
            label: 'Fingerprint Metadata',
            metadata: visualizationFingerprintMetadata,
        })
    }
    

    return visualizationMetadataMenuItems;
}


export const formatDisplayedReadacrossMetadata = async (
    readacross: ReadAcross,
) => {
    const editableParams = ['name', 'tags'];
    const readacrossMetadataMenuItems: MetadataMenuItemType[] = [];

    const {
        created_by,
        creation_date,
        dataset_hash,
        file_extension,
        name,
        owner,
        readacross_type,
        size,
        smiles,
        updated_date,
        readacross_metadata,
    } = readacross;

    const {
        tags,
        fingerprint_metadata,
    } = readacross_metadata!;

    const readacrossGeneralMetadata: MetadataInfo[] = [];

    const generalMetadata = {
        name,
        size: size ? formatBytes(size) : '',
        readacross_type,
        creation_date: creation_date ? formatDateString(creation_date) : '',
        updated_date: updated_date ? formatDateString(updated_date) : '',
        created_by,
        owner,
        file_extension,
        smiles,
        tags: tags ? Array.from(tags).map(tag => tag === null ? 'null' : tag as string) : [],
    };

    for (const [key, value] of Object.entries(generalMetadata)) {
        if (value !== undefined) {
            let metadataInfo: MetadataInfo = {
                label: splitLabel(key),
                value: value ?? '',
                type: 'text',
                colspan: 1,
                editableParam: undefined,
            };

            if (editableParams.includes(key)) {
                metadataInfo.editableParam = key;
            }

            if (['created_by', 'owner'].includes(key)) {
                metadataInfo.type = 'user';
            } else if (key === 'tags') {
                metadataInfo.type = 'tags';
                metadataInfo.colspan = 2;
            } else if (['name', 'smiles'].includes(key)) {
                metadataInfo.colspan = 2;
            }

            readacrossGeneralMetadata.push(metadataInfo);
        }
    }

    if (dataset_hash) {
        const response = await customInstance({
            url: `dataset/?dataset_hash=${dataset_hash}&include_dataset_metadata=false&include_airflow_metadata=false`,
            method: 'GET',
        });
        const dataset = response as unknown as Dataset;
        const datasetInfo: MetadataAssociatedFile = {
            type: 'dataset',
            name: dataset.name ?? '',
            hash: dataset_hash as string,
            size: dataset.size ? formatBytes(dataset.size) : undefined,
        };
        readacrossGeneralMetadata.push({
            label: 'Associated Dataset',
            value: [datasetInfo],
            type: 'associated_files',
            colspan : 2,
        });
    };

    readacrossMetadataMenuItems.push({
        label: 'General Metadata',
        metadata: readacrossGeneralMetadata,
    });

    if (fingerprint_metadata) {
        const readacrossFingerprintMetadata: MetadataInfo[] = [];

        const {
            job_metadata,
            runtime_environment,
            runtime_metrics
        } = fingerprint_metadata;

        let fingerprintMetadata: any = {};
        
        if (job_metadata) {
            const {
                calculate_structure_analysis,
                dataset,
                fingerprint_function_pooling_type,
                fingerprint_functions,
                output_filepath,
                output_fingerprint_column_name,
                output_structure_analysis_column_name,
            } = job_metadata;

            const fingerprintJobMetadata = {
                calculate_structure_analysis,
                fingerprint_function_pooling_type,
                output_filepath,
                output_fingerprint_column_name,
                output_structure_analysis_column_name,
            }

            if (fingerprint_functions && fingerprint_functions.length > 0) {
                const descriptors: string[] = [];
                let fingerprintParams: any = {};
                fingerprint_functions.forEach(func => {
                    if (func.name) {
                        descriptors.push(func.name)
                    };
                });
                
                if (descriptors.length > 0) {
                    descriptors.forEach(descriptor => {
                        const fingerprintFunction = fingerprint_functions.find(f => f.name && f.name === descriptor);
                        if (fingerprintFunction) {
                            for (const [key, value] of Object.entries(fingerprintFunction)) {
                                if (!['KIND', 'name'].includes(key) && value !== null) {
                                    fingerprintParams[`${descriptor} ${splitLabel(key)}`] = value;
                                }
                            }
                        }
                     });

                     fingerprintMetadata['descriptors'] = descriptors;
                };

                if (Object.keys(fingerprintParams).length > 0) {
                    for (const [key, value] of Object.entries(fingerprintParams)) {
                        fingerprintMetadata[key] = value;
                    }
                }

                if (dataset) {
                    for (const [key, value] of Object.entries(dataset)) {
                        fingerprintMetadata[`Dataset ${splitLabel(key)}`] = value ?? 'null';
                    }
                };

                for (const [key, value] of Object.entries(fingerprintJobMetadata)) {
                    if (value !== undefined) {
                        fingerprintMetadata[key] = value ?? '';
                    }
                };

            };
        };

        fingerprintMetadata['runtime_environment'] = runtime_environment;
        fingerprintMetadata['runtime_metrics'] = runtime_metrics;

        for (const [key, value] of Object.entries(fingerprintMetadata)) {
            if (value !== undefined) {
                let metadataInfo: MetadataInfo = {
                    label: splitLabel(key),
                    value: value ?? 'null',
                    type: typeof value === 'boolean' ? 'boolean' : 'text',
                    colspan: 1,
                };

                if (isPlainObject(value)) {
                    metadataInfo.type = 'json';
                    metadataInfo.colspan = 2;
                } else if (key === 'descriptors') {
                    metadataInfo.type = 'array';
                    metadataInfo.colspan = 2;
                } 

                readacrossFingerprintMetadata.push(metadataInfo);
            }
        };

        readacrossMetadataMenuItems.push({
            label: 'Fingerprint Metadata',
            metadata: readacrossFingerprintMetadata,
        })

    }

    return readacrossMetadataMenuItems;
}

export const formatDisplayedJobMetadata = async (
    job: IndividualAirflowJob,
) => {
    const jobMetadata: MetadataInfo[] = [];

    const {
        dag_id,
        end_date,
        execution_date,
        external_trigger,
        queued_at,
        run_id,
        run_type,
        start_date,
        state,
        user,
        dag_final_state_report,
    } = job;

    let generalMetadata = {
        dag_id,
        run_id,
        status: state,
        run_type,
        external_trigger,
        executed_by: '',
        queued_at: queued_at ? formatDateString(queued_at) : '',
        execution_date: execution_date ? formatDateString(execution_date) : '',
        start_date: start_date ? formatDateString(start_date) : '',
        end_date: end_date ? formatDateString(end_date) : '',
    }

    const createdResources: MetadataAssociatedFile[] = [];

    if (user && user.length > 0) {
        generalMetadata.executed_by = user[0].username;
    }

    if (dag_final_state_report) {
        const {
            dag_state,
            report,
        } = dag_final_state_report;

        if (dag_state) {
            generalMetadata.status = dag_state;
        }

        if (report && report.all_created_resources) {
            for (const [key, value] of Object.entries(report.all_created_resources)) {
                let promises: Promise<any>[] = [];
                if (key === 'model_family_id') {
                    promises = (value as number[]).map(async (id) => {
                        const response = await customInstance({
                            url: `model_family/?model_family_id=${id}&include_model_family_metadata=false&include_model_stats=false&include_airflow_metadata=false`,
                            method: 'GET',
                        });
                        const modelFamily = response as unknown as ModelFamily;
                        const modelFamilyInfo: MetadataAssociatedFile = {
                            type: 'model',
                            name: modelFamily.name ?? 'Unnamed Model Family',
                            hash: id.toString(),
                        };
                        return createdResources.push(modelFamilyInfo);
                    })
                } else if (key === 'dataset_hash') {
                    promises = (value as string[]).map(async (hash) => {
                        const response = await customInstance({
                            url: `dataset/?dataset_hash=${hash}&include_dataset_metadata=false&include_airflow_metadata=false`,
                            method: 'GET',
                        });
                        const dataset = response as unknown as Dataset;
                        const dsInfo: MetadataAssociatedFile = {
                            type: 'dataset',
                            name: dataset.name ?? 'Unnamed Dataset',
                            hash,
                            size: dataset.size ? formatBytes(dataset.size) : undefined,
                        }
                        return createdResources.push(dsInfo);
                    });
                } else if (key === 'resultset_id') {
                    promises = (value as number[]).map(async (id) => {
                        const response = await customInstance({
                            url: `resultset/?resultset_id=${id}`,
                            method: 'GET',
                        });
                        const rs = response as unknown as Resultset;
                        const rsInfo: MetadataAssociatedFile = {
                            type: 'resultset',
                            name: rs.name ?? 'Unnamed Resultset',
                            hash: id.toString(),
                        };
                        return createdResources.push(rsInfo);
                    })
                } else if (key === 'readacross_id') {
                    promises = (value as number[]).map(async (id) => {
                        const response = await customInstance({
                            url: `readacross/?readacross_id=${id}`,
                            method: 'GET',
                        });
                        const ra = response as unknown as ReadAcross;
                        const raInfo: MetadataAssociatedFile = {
                            type: 'readacross',
                            name: ra.name ?? 'Unnamed Read-Across',
                            hash: id.toString(),
                            size: ra.size ? formatBytes(ra.size) : undefined,
                        };
                        return createdResources.push(raInfo);
                    });
                };

                try {
                    await Promise.all(promises);
                } catch (err:any) {
                    console.log(err);
                }
            }
        }
    };

    for (const [key, value] of Object.entries(generalMetadata)) {
        let metadataInfo: MetadataInfo = {
            label: splitLabel(key),
            value: value ?? '',
            type: typeof value === 'boolean' ? 'boolean' : 'text',
            colspan: 1,
        };

        if (['dag_id', 'run_id'].includes(key)) {
            metadataInfo.colspan = 2;
        } else if (key === 'executed_by') {
            metadataInfo.type = 'user';
        } else if (key === 'status') {
            metadataInfo.type = 'state';
        }

        jobMetadata.push(metadataInfo);
    };

    if (createdResources.length > 0) {
        jobMetadata.push({
            label: 'Created Resources',
            value: createdResources,
            type: 'associated_files',
            colspan: 2,
        })
    };

    const jobMetadataMenuItem: MetadataMenuItemType = {
        label: 'General Metadata',
        metadata: jobMetadata,
    };

    return [jobMetadataMenuItem];

}


// EXPORT METADATA FORMATTING FUNCTIONS

export const formatModelMetadata = async (
    model: Model,
    methodName: string,
) => {
    const {
        created_by,
        creation_date,
        dataset_hash,
        file_metadata,
        legacy,
        name,
        owner,
        size,
        updated_date,
        model_metadata,
    } = model;

    const tags = file_metadata && file_metadata.tags ? file_metadata.tags : [];

    const data: (string | number | boolean)[][] = [];

    const response = await customInstance({
        url: `dataset/?dataset_hash=${dataset_hash}`,
        method: 'GET',
    });

    const dataset = response as unknown as Dataset;

    if (dataset) {
        data.push(['Training File', dataset.name ?? '']);
    }

    data.push(['Model Family', name ?? '']);
    data.push(['Method', methodName]);
    data.push(['']);
    data.push(['Model Metadata']);
    data.push(['']);

    const generalMetadata = {
        created_by,
        owner,
        creation_date,
        updated_date,
        size,
        legacy,
        tags,
    };

    for (const [key, value] of Object.entries(generalMetadata)) {
        if (value !== null && value !== undefined) {
            if (Array.isArray(value)) {
                 value.forEach((val, i) => {
                    let rowValues = [key, val as string];

                    if (i > 0) {
                        rowValues = ['', val as string];
                    }

                    data.push(rowValues);
                 })
            } else {
                let cellValue = value ?? '';

                if (value !== null && value !== undefined) {
                    if (key === 'size') {
                        cellValue = formatBytes(Number(value));
                    } else if (key.includes('date')) {
                        cellValue = new Date(value as string).toLocaleString('en-US', {timeZone: 'EST'})
                    }
                }

                data.push([key, cellValue])
            }
        }
    };

    if (model_metadata) {
        data.push(['']);
        const {
            training_metadata,
            fingerprint_metadata,
            nested_cv_metadata,
        } = model_metadata;
        
        const metadataList: {
            label: string,
            metadataObj: {[key: string]: any},
        }[] = [];

        if (fingerprint_metadata && fingerprint_metadata.job_metadata) {

            const {
                calculate_structure_analysis,
                dataset,
                fingerprint_function_pooling_type,
                fingerprint_functions,
                output_filepath,
                output_fingerprint_column_name,
                output_structure_analysis_column_name,
            } = fingerprint_metadata.job_metadata;

            const fingerprintRuntimeMetrics = fingerprint_metadata.runtime_metrics;

            const fingerprintJobMetadata = {
                fingerprint_functions,
                fingerprint_function_pooling_type,
                dataset,
                calculate_structure_analysis,
                output_filepath,
                output_fingerprint_column_name,
                output_structure_analysis_column_name,
                runtime_metrics: fingerprintRuntimeMetrics,
            };

            metadataList.push({
                label: 'Fingerprint Metadata',
                metadataObj: fingerprintJobMetadata,
            });
        }
        
        if (nested_cv_metadata && nested_cv_metadata.job_metadata) {
            const {
                aggregation_functions,
                cv_model,
                data_augmentation_functions,
                evaluation_cv_splits,
                evaluation_functions,
                featureset,
                training_augmentation_functions,
            } = nested_cv_metadata.job_metadata;

            const nestedCVRuntimeMetrics = nested_cv_metadata.runtime_metrics;

            const nestedCVJobMetadata = {
                aggregation_functions,
                cv_model,
                data_augmentation_functions,
                evaluation_cv_splits,
                evaluation_functions,
                featureset,
                training_augmentation_functions,
                runtime_metrics: nestedCVRuntimeMetrics,
            }

            metadataList.push({
                label: 'Nested CV Metadata',
                metadataObj: nestedCVJobMetadata,
            });
        }

        if (training_metadata && training_metadata.job_metadata) {
            const {
                cv_model,
                data_augmentation_functions,
                featureset,
                holdout_evaluation_functions,
                holdout_splitter,
                keep_additional_holdout_set,
                training_augmentation_functions,
            } = training_metadata.job_metadata;

            const trainingRuntimeMetrics = training_metadata.runtime_metrics;

            const trainingJobMetadata = {
                cv_model,
                data_augmentation_functions,
                featureset,
                holdout_evaluation_functions,
                holdout_splitter,
                keep_additional_holdout_set,
                training_augmentation_functions,
                runtime_metrics: trainingRuntimeMetrics,
            };

            metadataList.push({
                label: 'Training Metadata',
                metadataObj: trainingJobMetadata,
            });
        }

        if (metadataList.length > 0) {
            metadataList.forEach(metadata => {
                const { label, metadataObj } = metadata;

                data.push([label]);

                for (const [key, value] of Object.entries(metadataObj)) {
                    if (value !== undefined) {
                        if (value === null) {
                            data.push([key, 'null'])
                        } else if (Array.isArray(value)) {
                            value.forEach((arrValue, index) => {
                                const rowValues = index > 0 ? [''] : [key];
                                if (isPlainObject(arrValue)) {
                                    for (const [arrIndex, [arrObjKey, arrObjVal]] of Object.entries(arrValue as any).entries()) {
                                        if (arrIndex > 0) {
                                            rowValues.push('');
                                        }
                                        rowValues.push(`${arrObjKey}:`);
                                        rowValues.push(arrObjVal as any);
                                    };
                                } else {
                                    rowValues.push(arrValue as any ?? 'null');
                                }

                                data.push(rowValues);
                            })
                        } else if (isPlainObject(value)) {
                            data.push([key]);
                            for (const [objKey, objValue] of Object.entries(value)) {
                                if (isPlainObject(objValue)) {
                                    data.push(['', objKey]);

                                    for (const [nestedObjKey, nestedObjValue] of Object.entries(objValue as any)) {
                                        if (isPlainObject(nestedObjValue)) {
                                            data.push(['', '', objKey]);

                                            for (const [deeplyNestedObjKey, deeplyNestedObjValue] of Object.entries(nestedObjValue as any)) {
                                                data.push(['', '', '', deeplyNestedObjKey, (deeplyNestedObjValue as any) ?? 'null']);
                                            }
                                        } else {
                                            data.push(['', '', nestedObjKey, (nestedObjValue as any) ?? 'null']);
                                        }
                                    }
                                } else {
                                    data.push(['', objKey, (objValue as any) ?? 'null'])
                                }
                            }
                        } else {
                            data.push([key, value as string | boolean | number])
                        }
                    }
                };

                data.push(['']);
                
            });
        }
    }

    return data;
}

export interface BatchPredictionMetadataFormattingOptions {
    modelFamilyId?: number,
    includeModelFamilyDatasetMetadata?: boolean,
}

export const formatBatchPredictionMetadata = async (
    predictions: Prediction[],
    modelFamilyName: string,
    modelHeaders: TableHeader[],
    options: BatchPredictionMetadataFormattingOptions = {},
) => {
    const {
        created_by,
        dataset_hash,
        owner,
        file_metadata,
        prediction_metadata,
    } = predictions[0];

    const tags = file_metadata && file_metadata.tags ? file_metadata.tags : [];
    const prediction_type = prediction_metadata && prediction_metadata.prediction_type ? prediction_metadata.prediction_type : '';

    const headerData: string[][] = [];

    const response = await customInstance({
        url: `dataset/?dataset_hash=${dataset_hash}`,
        method: 'GET',
    });

    const dataset = response as unknown as Dataset;

    if (dataset) {
        headerData.push(['Test File', dataset.name ?? '']);
    }

    headerData.push(['Model Family', modelFamilyName]);

    if (options.includeModelFamilyDatasetMetadata) {
        let modelFamilyDatasetHash = '';
        let modelFamilyDatasetName = '';
        let modelFamilyDatasetDescription = '';

        try {
            if (options.modelFamilyId !== undefined) {
                const modelResponse = await customInstance({
                    url: `model_family/models/${options.modelFamilyId}`,
                    method: 'GET',
                });

                const models = modelResponse as unknown as Model[];
                modelFamilyDatasetHash = models[0]?.dataset_hash ?? '';
            }

            if (modelFamilyDatasetHash) {
                const modelFamilyDatasetResponse = await customInstance({
                    url: `dataset/?dataset_hash=${modelFamilyDatasetHash}`,
                    method: 'GET',
                });

                const modelFamilyDataset = modelFamilyDatasetResponse as unknown as Dataset;
                modelFamilyDatasetName = modelFamilyDataset.name ?? '';
                modelFamilyDatasetDescription = (modelFamilyDataset.dataset_metadata as any)?.description ?? '';
            }
        } catch (err:any) {
            console.log(err);
        }

        headerData.push(['Training Dataset', modelFamilyDatasetName || 'N/A']);
        headerData.push(['Training Dataset Hash', modelFamilyDatasetHash || 'N/A']);
        headerData.push(['Training Dataset Description', modelFamilyDatasetDescription || 'N/A']);
    }

    headerData.push(['']);

    const generalMetadata = {
        created_by,
        owner,
        tags,
        prediction_type,
    };

    for (const [key, value] of Object.entries(generalMetadata)) {
        if (value !== null && value !== undefined) {
            if (Array.isArray(value)) {
                 value.forEach((val, i) => {
                    let rowValues = [key, val as string];

                    if (i > 0) {
                        rowValues = ['', val as string];
                    }

                    headerData.push(rowValues);
                 })
            } else {
                headerData.push([key, value])
            }
        }
    };

    headerData.push(['']);
    headerData.push(['Predictions Metadata']);
    headerData.push(['']);

    const allPredMetadata: {
        name: string,
        id: string,
        data: string[][];
    }[] = [];

    modelHeaders.forEach(header => {
        const pred = predictions.find(p => p.model_hash === header.value);
        if (pred) {
            let predMetadata: {
                name: string,
                id: string,
                data: string[][],
            } = {
                name: header.label,
                id: header.value,
                data: [],
            };

            const data: string[][] = [];

            const {
                creation_date,
                updated_date,
                size,
                prediction_metadata,
            } = pred;

            const {
                runtime_metrics,
                structure_column,
                job_metadata,
            } = prediction_metadata!;

            const predGeneralMetadata = {
                creation_date,
                updated_date,
                size,
                structure_column,
            };

            for (const [key, value] of Object.entries(predGeneralMetadata)) {
                if (value !== null && value !== undefined) {
                    let cellValue = value ?? '';

                    if (value !== null && value !== undefined) {
                        if (key === 'size') {
                            cellValue = formatBytes(Number(value));
                        } else if (key.includes('date')) {
                            cellValue = new Date(value).toLocaleString('en-US', {timeZone: 'EST'})
                        }
                    }

                    data.push([key, cellValue.toString()])
                }
            };

            data.push(['']);

            if (job_metadata) {
                data.push(['Job Metadata']);

                const {
                    experiment,
                    experiment_id,
                    experiment_name,
                    featureset,
                    ml_model_file_location,
                    model,
                    run_id,
                } = job_metadata;

                const predJobMetadata = {
                    experiment,
                    experiment_id,
                    experiment_name,
                    featureset,
                    ml_model_file_location,
                    model,
                    run_id,
                    runtime_metrics,
                }

                for (const [key, value] of Object.entries(predJobMetadata)) {
                    if (value !== null || value !== undefined) {
                        if (isPlainObject(value)) {
                            data.push([key]);
                            for (const [objectKey, objectValue] of Object.entries(value as any)) {
                                let val = objectValue as any;

                                if (objectValue === null || objectValue === undefined) {
                                    val = 'null';
                                } else if (key === 'runtime_metrics') {
                                    val = `${convertNumberToDecimal(objectValue as number)}s`;
                                }
                                data.push(['', objectKey, val])
                            }
                        } else {
                            data.push([key, value as string]);
                        }
                    }
                }
            }
            
            allPredMetadata.push({
                ...predMetadata,
                data,
            });
        }
    });

    return {
        headerData,
        allPredMetadata,
    }
}

export const formatDatasetMetadata = async (dataset: Dataset) => {

    const {
        created_by,
        creation_date,
        dataset_metadata,
        file_metadata,
        owner,
        parent_data_file_hash,
        size,
        updated_date,
    } = dataset;

    const {
        column_metadata,
        dataset_types,
        description,
        records_number,
        stages
    } = dataset_metadata!;

    const tags = file_metadata && file_metadata.tags ? file_metadata.tags : [];

    const generalMetadata = {
        'created_by': created_by,
        'owner': owner,
        'creation_date': creation_date,
        'updated_date': updated_date,
        'size': size,
        'description': description,
        'records_number': records_number,
        'tags': tags,
        'dataset_types': dataset_types,
    };

    let datafileName = '';

    if (parent_data_file_hash) {
        try {
            const response = await customInstance({
                url: `data_file/?data_file_hash=${parent_data_file_hash}`,
                method: 'GET',
            });

            const file = response as unknown as DataFile;
            if (file) {
                datafileName = file.name ?? '';
            }

        } catch (err:any) {
            console.log(err);
        }
    };

    const data: (string | number | boolean)[][] = [
        ['Parent Datafile Name', datafileName],
        [''],
        ['Dataset Metadata'],
        [''],
    ];


    for (const [key, value] of Object.entries(generalMetadata)) {
        if (value !== null && value !== undefined) {
            if (Array.isArray(value)) {
                 value.forEach((val, i) => {
                    let rowValues = [key, val as string];

                    if (i > 0) {
                        rowValues = ['', val as string];
                    }

                    data.push(rowValues);
                 })
            } else {
                let cellValue = value ?? '';

                if (value !== null && value !== undefined) {
                    if (key === 'size') {
                        cellValue = formatBytes(Number(value));
                    } else if (key.includes('date')) {
                        cellValue = new Date(value).toLocaleString('en-US', {timeZone: 'EST'})
                    }
                }

                data.push([key, cellValue])
            }
        }
    }

    if (column_metadata) {
        data.push(['']);
        data.push(['Column Metadata']);
        for (const [columnKey, columnValue] of Object.entries(column_metadata)) {
            if (columnValue !== null) {
                data.push([columnKey])
                for (const [key, value] of Object.entries(columnValue)) {
                    data.push(['', key, value ?? 'null'])
                }
            }
        }
    };

    if (stages && stages.length > 0) {
        data.push(['']);
        data.push(['Stages']);
        stages.forEach(stage => {
            data.push(['function', stage.function ?? '']);

            if (stage.function_params && Object.keys(stage.function_params).length > 0) {
                data.push(['function_params']);
                for (const [key, value] of Object.entries(stage.function_params)) {
                    data.push(['', key, (value as any) ?? 'null']);
                }
            }
            data.push(['']);
        })
    }

    return data;

}

export const formatReadacrossMetadata = async (readacross: ReadAcross) => {
    const {
        created_by,
        creation_date,
        dataset_hash,
        id,
        name,
        owner,
        readacross_metadata,
        readacross_type,
        size,
        smiles,
        updated_date,
    } = readacross;

    const {
        fingerprint_metadata,
        tags,
    } = readacross_metadata!;

    const headingData: (string | number | boolean)[][] = [
        ['Readacross File Name', name ?? ''],
        ['Readacross File ID', id ?? ''],
        ['Readacross Type', readacross_type ?? ''],
        [''],
        ['Input SMILES', smiles ?? ''],
    ];

    if (dataset_hash) {
        const response = await customInstance({
            url: `dataset/?dataset_hash=${dataset_hash}`,
            method: 'GET',
        });

        const dataset = response as unknown as Dataset;

        if (dataset) {
            headingData.push(['Dataset Name', dataset.name ?? '']);
        };
    };

    headingData.push(['']);
    headingData.push(['Readacross Metadata']);
    headingData.push(['']);

    const data: (string | number | boolean)[][] = [];

    const generalMetadata = {
        created_by,
        owner,
        creation_date,
        updated_date,
        size,
        tags,
    };

    for (const [key, value] of Object.entries(generalMetadata)) {
        if (value !== null && value !== undefined) {
            if (Array.isArray(value)) {
                 value.forEach((val, i) => {
                    let rowValues = [key, val as string];

                    if (i > 0) {
                        rowValues = ['', val as string];
                    }

                    data.push(rowValues);
                 })
            } else {
                let cellValue = value ?? '';

                if (value !== null && value !== undefined) {
                    if (key === 'size') {
                        cellValue = formatBytes(Number(value));
                    } else if (key.includes('date')) {
                        cellValue = new Date(value).toLocaleString('en-US', {timeZone: 'EST'})
                    }
                }

                data.push([key, cellValue])
            }
        }
    };

    if (fingerprint_metadata) {
        data.push(['']);
        data.push(['Fingerprint Metadata']);

        const {
            job_metadata,
            runtime_metrics,
        } = fingerprint_metadata;

        if (job_metadata) {
            const {
                calculate_structure_analysis,
                dataset,
                fingerprint_function_pooling_type,
                fingerprint_functions,
                output_filepath,
                output_fingerprint_column_name,
                output_structure_analysis_column_name,
            } = job_metadata;

            const jobMetadataObj = {
                calculate_structure_analysis,
                dataset,
                fingerprint_function_pooling_type,
                fingerprint_functions,
                output_filepath,
                output_fingerprint_column_name,
                output_structure_analysis_column_name,
            }

            for (const [key, value] of Object.entries(jobMetadataObj)) {
                if (value !== undefined) {
                    if (value === null) {
                        data.push([key, 'null'])
                    } else if (Array.isArray(value)) {
                        value.forEach((arrValue, index) => {
                            const rowValues = index > 0 ? [''] : [key];
                            if (isPlainObject(arrValue)) {
                                for (const [arrIndex, [arrObjKey, arrObjVal]] of Object.entries(arrValue as any).entries()) {
                                    if (arrIndex > 0) {
                                        rowValues.push('');
                                    }
                                    rowValues.push(`${arrObjKey}:`);
                                    rowValues.push(arrObjVal as any);
                                };
                            } else {
                                rowValues.push(arrValue as any ?? 'null');
                            }

                            data.push(rowValues);
                        })
                    } else if (isPlainObject(value)) {
                        data.push([key]);
                        for (const [objKey, objValue] of Object.entries(value)) {
                            if (isPlainObject(objValue)) {
                                data.push(['', objKey]);

                                for (const [nestedObjKey, nestedObjValue] of Object.entries(objValue as any)) {
                                    if (isPlainObject(nestedObjValue)) {
                                        data.push(['', '', objKey]);

                                        for (const [deeplyNestedObjKey, deeplyNestedObjValue] of Object.entries(nestedObjValue as any)) {
                                            data.push(['', '', '', deeplyNestedObjKey, (deeplyNestedObjValue as any) ?? 'null']);
                                        }
                                    } else {
                                        data.push(['', '', nestedObjKey, (nestedObjValue as any) ?? 'null']);
                                    }
                                }
                            } else {
                                data.push(['', objKey, (objValue as any) ?? 'null'])
                            }
                        }
                    } else {
                        data.push([key, value as string | boolean | number])
                    }
                }
            };

            data.push(['']);
        };

        if (runtime_metrics) {
            data.push(['runtime_metrics']);
            for (const [key, value] of Object.entries(runtime_metrics)) {
                data.push(['', key, value ? convertNumberToDecimal(value as string | number) : 'null']);
            }
        }
    }

    return {
        headingData,
        data,
    }
}

export const formatResultsetVisualizationMetadata = (visualization: ResultsetVisualizationType, resultset: Resultset) => {
    
    const metadata = visualization.metadata;
    const resultset_name = resultset.name ?? '';

    const {
        creation_date,
        updated_date,
        dataset_visualization_metadata,
    } = metadata;

    const visualization_method_metadata = dataset_visualization_metadata.visualization_method_metadata;

    const {
        method_type,
        params,
    } = visualization_method_metadata;

    const fingerprint_metadata = (dataset_visualization_metadata as any).fingerprint_metadata;

    const headingRows: (string | number | boolean)[][] = [
        ['Resultset Name', resultset_name],
        ['Method Type', method_type],
    ];

    for (const [paramKey, paramValue] of Object.entries(params)) {
        headingRows.push([paramKey, paramValue]);
    };

    headingRows.push(['']);
    headingRows.push(['Resultset Visualization Metadata']);
    headingRows.push(['']);

    const metadataRows: (string | number | boolean)[][] = [
        ['creation_date', creation_date ? formatDateString(creation_date) : ''],
        ['updated_date', updated_date ? formatDateString(updated_date) : ''],
        ['']
    ];

    if (fingerprint_metadata) {
        metadataRows.push(['Fingerprint Metadata']);

        if (fingerprint_metadata.job_metadata) {
            const {
                calculate_structure_analysis,
                dataset,
                fingerprint_function_pooling_type,
                fingerprint_functions,
                output_filepath,
                output_fingerprint_column_name,
                output_structure_analysis_column_name,
            } = fingerprint_metadata.job_metadata;

            const fingerprintRuntimeMetrics = fingerprint_metadata.runtime_metrics;

            const fingerprintJobMetadata = {
                fingerprint_functions,
                fingerprint_function_pooling_type,
                dataset,
                calculate_structure_analysis,
                output_filepath,
                output_fingerprint_column_name,
                output_structure_analysis_column_name,
                runtime_metrics: fingerprintRuntimeMetrics,
            };

            for (const [key, value] of Object.entries(fingerprintJobMetadata)) {
                if (value !== undefined) {
                    if (value === null) {
                        metadataRows.push([key, 'null'])
                    } else if (Array.isArray(value)) {
                        value.forEach((arrValue, index) => {
                            const rowValues = index > 0 ? [''] : [key];
                            if (isPlainObject(arrValue)) {
                                for (const [arrIndex, [arrObjKey, arrObjVal]] of Object.entries(arrValue as any).entries()) {
                                    if (arrIndex > 0) {
                                        rowValues.push('');
                                    }
                                    rowValues.push(`${arrObjKey}:`);
                                    rowValues.push(arrObjVal as any);
                                };
                            } else {
                                rowValues.push(arrValue as any ?? 'null');
                            }

                            metadataRows.push(rowValues);
                        })
                    } else if (isPlainObject(value)) {
                        metadataRows.push([key]);
                        for (const [objKey, objValue] of Object.entries(value)) {
                            if (isPlainObject(objValue)) {
                                metadataRows.push(['', objKey]);

                                for (const [nestedObjKey, nestedObjValue] of Object.entries(objValue as any)) {
                                    if (isPlainObject(nestedObjValue)) {
                                        metadataRows.push(['', '', objKey]);

                                        for (const [deeplyNestedObjKey, deeplyNestedObjValue] of Object.entries(nestedObjValue as any)) {
                                            metadataRows.push(['', '', '', deeplyNestedObjKey, (deeplyNestedObjValue as any) ?? 'null']);
                                        }
                                    } else {
                                        metadataRows.push(['', '', nestedObjKey, (nestedObjValue as any) ?? 'null']);
                                    }
                                }
                            } else {
                                metadataRows.push(['', objKey, (objValue as any) ?? 'null'])
                            }
                        }
                    } else {
                        metadataRows.push([key, value as string | boolean | number])
                    }
                }
            };
        };
    };

    return {
        headingRows,
        metadataRows,
    }


}

export const formatResultsetDataframe = async (
    dataframe: { [key: string]: any },
    predictions?: Prediction[],
    use_proba: boolean = false,
    options: ResultsetDataframeFormattingOptions = {},
) => {
    const dcmPredictionChannelKeys = [
        'y_prob_out_of_domain',
        'y_prob_1_exp',
        'y_prob_1_min',
        'y_prob_1_low',
        'y_prob_1_mid',
        'y_prob_1_high',
        'y_prob_1_max',
        'y_prob_binary',
        'y_prob_ambiguous',
        'y_prob_active',
        'y_prob_inactive',
    ];

    const groupedRsData: ResultsetGrouping[] = [];
    const predEvalMetadata: ResultsetEvaluationMetadata[] = [];
    const moleculeHighlightingMethodFilter = (options.moleculeHighlightingMethodFilter ?? '').trim().toLowerCase();
    const includeFeatureImportanceMethodInHighlightingLabel = options.includeFeatureImportanceMethodInHighlightingLabel ?? false;

    const model_family_mapping = dataframe.model_family_mapping ?? {};
    const model_hash_mapping = dataframe.model_hash ?? {};
    const source_dataset_hash_mapping = dataframe.source_dataset_hash ?? {};
    const smiles_mapping = dataframe['structure/SMILES'] ?? [] as string[];

    const columnMetadata = Object.entries(dataframe).filter(df => {
        let isColumnMetadataKey = false;
        ['chemical-name', 'record-id'].forEach(metadataKey => {
            if (df[0].includes(metadataKey)) {
                isColumnMetadataKey = true;
            }
        });
        return isColumnMetadataKey;
    });

    if (Object.keys(model_family_mapping).length > 0 && smiles_mapping.length > 0) {
        for (const [model_family_id, model_family_value] of Object.entries(model_family_mapping)) {
            let rsData: ResultsetGrouping = {
                modelFamilyId: Number(model_family_id),
                modelFamilyName: '',
                isConformal: false,
                hasDcm: false,
                dcmModelNames: [],
                dcmModelHashes: [],
                metricsHeaders: [],
                modelHeaders: [],
                moleculeHighlightingHeaders: [],
                data: [],
                hasProba: false,
            };

            const metricsHeaders: TableHeader[] = [
                {
                    label: 'Structure',
                    value: 'structure/SMILES',
                    isShown: true,
                    alignment: 'left',
                },
                {
                    label: 'SMILES',
                    value: 'smiles',
                    isShown: true,
                    alignment: 'center',
                }
            ];

            let model_family_values: { [key: string]: any[] } = {};

            if (columnMetadata.length > 0) {
                columnMetadata.forEach(column => {
                    const label = column[0].split('/')[1];
                    metricsHeaders.push({
                        label,
                        value: column[0],
                        isShown: true,
                        alignment: 'center',
                    });

                    model_family_values[column[0]] = column[1];
                })
            };

            const modelHeaders: TableHeader[] = [];
            const moleculeHighlightingHeaders: TableHeader[] = [];
            const dcmHiddenFieldKeys: string[] = [];
            const data: TableData[] = [];


            const model_family_mapping_value = model_family_value as any;

            const metrics = model_family_mapping_value.metrics ?? {};
            const model_hashes = model_family_mapping_value.model_hashes ?? [] as string[];
            const source_dataset_hash = model_family_mapping_value.source_dataset_hash ?? '';

            const source_dataset_metrics = source_dataset_hash_mapping[source_dataset_hash] ?? {};
            const applicability_domain_mapping = source_dataset_metrics.applicability_domains ?? {};
            const duplicates_mapping = source_dataset_metrics.duplicate ?? {};

            Object.keys(applicability_domain_mapping).length > 0 && Object.keys(applicability_domain_mapping).forEach(key => {
                model_family_values[key] = applicability_domain_mapping[key];
                metricsHeaders.push({
                    label: key.split('/')[0],
                    value: key,
                    isShown: true,
                    alignment: 'center',
                });
            });

            Object.keys(duplicates_mapping).length > 0 && Object.keys(duplicates_mapping).forEach(key => {
                model_family_values[key] = duplicates_mapping[key];
                metricsHeaders.push({
                    label: key,
                    value: key,
                    isShown: true,
                    alignment: 'center',
                });
            });

            const metrics_y_pred = use_proba ? metrics.y_pred_proba ?? metrics.y_pred ?? {} : metrics.y_pred ?? {};
            if (metrics.y_pred_proba) {
                rsData.hasProba = true;
            }

            Object.keys(metrics_y_pred).length > 0 && Object.keys(metrics_y_pred).forEach(key => {
                model_family_values[key] = metrics_y_pred[key];
                metricsHeaders.push({
                    label: key,
                    value: key,
                    isShown: true,
                    alignment: 'center',
                })
            });            

            const modelFamilyResponse = await customInstance({
                url: `/model_family/?model_family_id=${model_family_id}&include_model_family_metadata=false&include_model_stats=false&include_airflow_metadata=false`,
                method: 'GET',
            });

            const modelFamily = modelFamilyResponse as unknown as ModelFamily;
            const methodNamesAndIds: { name: string, id: string, isDcm: boolean }[] = [];

            rsData.modelFamilyName = modelFamily.name ?? '';
            rsData.hasDcm = Boolean(modelFamily.contains_dcm);

            // Use precomputed model_method_names from dataframe_v2 response if available
            const modelMethodNames = dataframe.model_method_names ?? {};

            if (model_hashes.length > 0) {
                for (const hash of model_hashes as string[]) {
                    const cached = modelMethodNames[hash];
                    if (cached) {
                        methodNamesAndIds.push({ name: cached.name ?? 'UnknownModel', id: hash, isDcm: !!cached.is_dcm });
                    } else {
                        // Fallback: fetch individually (for older responses without model_method_names)
                        const response = await customInstance({
                            url: `model/?model_hash=${hash}`,
                            method: 'GET',
                        });
                        const model = response as unknown as Model;
                        const modelMetadata = (model.model_metadata ?? {}) as any;
                        const name = modelMetadata.training_metadata?.job_metadata?.cv_model?.ml_base_cls?.name ?? model.ml_method ?? 'UnknownModel';
                        const isDcm = model.is_dcm ?? !!modelMetadata.dcm_metadata;
                        methodNamesAndIds.push({ name, id: hash, isDcm });
                    }
                }
            };

            // if (models.length > 0 && model_hashes.length > 0) {
            //     (model_hashes as string[]).forEach(hash => {
            //         const matchingModel = models.find(model => model.file_hash! === hash);
            //         if (matchingModel) {
            //             const name = matchingModel.model_metadata!.training_metadata!.job_metadata!.cv_model!.ml_base_cls!.name!;
            //             methodNamesAndIds.push({ name, id: matchingModel.file_hash! })
            //         }
            //     });
            // }

            if (methodNamesAndIds.length > 0) {
                methodNamesAndIds.sort((a,b) => {
                    if (a.name < b.name) {
                        return -1;
                    } else if (a.name > b.name) {
                        return 1;
                    } else {
                        return 0;
                    }
                });

                methodNamesAndIds.forEach(method => {
                    modelHeaders.push({
                        label: method.isDcm ? `${method.name} (DCM)` : method.name,
                        value: method.id,
                        isShown: true,
                        alignment: 'center'
                    })
                });

                const dcmMethodNames = methodNamesAndIds
                    .filter(method => method.isDcm)
                    .map(method => method.name);

                const dcmModelHashes = methodNamesAndIds
                    .filter(method => method.isDcm)
                    .map(method => method.id);

                if (dcmMethodNames.length > 0 || dcmModelHashes.length > 0) {
                    rsData.hasDcm = true;
                    rsData.dcmModelNames = Array.from(new Set(dcmMethodNames));
                    rsData.dcmModelHashes = Array.from(new Set(dcmModelHashes));
                }

                predictions !== undefined && methodNamesAndIds.forEach(method => {
                    const matchingPrediction = predictions.find(pred => pred.model_hash! === method.id);
                    if (matchingPrediction) {
                        if (matchingPrediction.evaluation_metadata && matchingPrediction.evaluation_metadata.evaluation_metrics && matchingPrediction.evaluation_metadata.evaluation_metrics['statistics']) {
                            const stats = matchingPrediction.evaluation_metadata.evaluation_metrics['statistics'] as any[];
                            if (stats.length > 0) {
                                predEvalMetadata.push({
                                    name: method.name,
                                    predictionId: matchingPrediction.file_hash!,
                                    evaluationMetadata: matchingPrediction.evaluation_metadata,
                                    modelFamilyId: Number(model_family_id),
                                });
                            }
                        }
                    }
                });

                methodNamesAndIds.forEach(method => {
                    const matchingMethod = Object.entries(model_hash_mapping).find(mapping => mapping[0] === method.id);
                    if (matchingMethod) {
                        const method_obj = matchingMethod[1] as any;

                        const predictions = method_obj.predictions ?? {};
                        const method_molecule_highlighting = method_obj.molecule_highlighting ?? [];
                        const hasConformalOutput = Array.isArray(predictions.y_conf_pred) && predictions.y_conf_pred.length > 0;

                        if (hasConformalOutput) {
                            rsData.isConformal = true;
                        }

                        if (method.isDcm) {
                            dcmPredictionChannelKeys.forEach(channelKey => {
                                const channelValues = predictions[channelKey];
                                if (Array.isArray(channelValues)) {
                                    const hiddenFieldKey = `dcm/${method.id}/${channelKey}`;
                                    model_family_values[hiddenFieldKey] = channelValues;
                                    if (!dcmHiddenFieldKeys.includes(hiddenFieldKey)) {
                                        dcmHiddenFieldKeys.push(hiddenFieldKey);
                                    }
                                }
                            });
                        }

                        const method_y_pred = hasConformalOutput
                            ? predictions.y_conf_pred
                            : method.isDcm
                                ? (
                                    use_proba
                                        ? (predictions.y_prob_1_exp ?? predictions.y_pred_proba ?? predictions.y_pred)
                                        : (predictions.y_pred ?? predictions.y_prob_1_exp)
                                )
                                : (use_proba && predictions.y_pred_proba ? predictions.y_pred_proba : predictions.y_pred ? predictions.y_pred : predictions.y_conf_pred);

                        if (method_y_pred) {
                            model_family_values[method.id] = method_y_pred;
                        };

                        if (method_molecule_highlighting.length > 0) {
                            (method_molecule_highlighting as any[]).forEach(highlighting => {
                                const atom_bond_importance = highlighting.atom_bond_importance ?? [];
                                const jobMetadata = highlighting?.metadata?.job_metadata ?? {};
                                const featureImportanceType = jobMetadata.feature_importance_type as string | undefined;
                                const featureImportanceMethod = jobMetadata.feature_importance_method as string | undefined;
                                const normalizedFeatureImportanceMethod = (featureImportanceMethod ?? '').trim().toLowerCase();

                                if (moleculeHighlightingMethodFilter && normalizedFeatureImportanceMethod !== moleculeHighlightingMethodFilter) {
                                    return;
                                }

                                if (featureImportanceType && atom_bond_importance) {
                                    const key = `${method.id}/atom_bond_importance/${featureImportanceType}`;
                                    model_family_values[key] = atom_bond_importance as any[];

                                    const methodLabel = featureImportanceMethod ?? 'unknown-method';
                                    const typeLabel = featureImportanceType ?? 'unknown-type';
                                    const label = includeFeatureImportanceMethodInHighlightingLabel
                                        ? `${method.name} (${methodLabel} (${typeLabel}))`
                                        : `${method.name} (${typeLabel})`;

                                    moleculeHighlightingHeaders.push({
                                        label,
                                        value: key,
                                        alignment: 'center',
                                        isShown: true,
                                    })
                                }
                            })
                        };
                    };
                });
            };

            (smiles_mapping as string[]).forEach((smiles, smilesIndex) => {
                const fields: TableDataField[] = [];

                metricsHeaders.forEach((header, i) => {
                    let field: TableDataField = {
                        associatedColumn: header.value,
                        alignment: i === 0 ? 'left' : 'center',
                        value: '',
                        type: 'text',
                    }
                    if (i === 0) {
                        field.value = smiles;
                        field.type = 'structure';
                    } else if (i === 1) {
                        field.value = smiles;
                    } else {
                        const model_family_value = model_family_values[header.value];
                        if (model_family_value) {
                            field.value = model_family_value[smilesIndex] ?? '';
                            if (header.value === 'is_duplicate') {
                                field.type = 'boolean';
                            } else if (columnMetadata.length > 0 && columnMetadata.map(m => m[0]).includes(header.value)) {
                                field.type = 'text';
                            } else {
                                field.type = 'stat';
                            }
                        }
                    };

                    if (field.value === '') {
                        field.type = 'text';
                    }

                    fields.push(field);
                });

                modelHeaders.length > 0 && modelHeaders.forEach(header => {
                    let field: TableDataField = {
                        associatedColumn: header.value,
                        alignment: 'center',
                        value: '',
                        type: 'stat',
                    }
                    const model_family_value = model_family_values[header.value];
                    if (model_family_value) {
                        field.value = model_family_value[smilesIndex] ?? '';
                    }

                    if (field.value === '') {
                        field.type = 'text';
                    }
                    fields.push(field);
                });

                moleculeHighlightingHeaders.length > 0 && moleculeHighlightingHeaders.forEach(header => {
                    let field: TableDataField = {
                        associatedColumn: header.value,
                        alignment: 'center',
                        value: '',
                        type: 'stat',
                    }
                    const model_family_value = model_family_values[header.value];
                    if (model_family_value) {
                        field.value = model_family_value[smilesIndex];
                    }

                    if (field.value === '') {
                        field.type = 'text';
                    }

                    fields.push(field);
                });

                dcmHiddenFieldKeys.forEach((fieldKey) => {
                    const hiddenValues = model_family_values[fieldKey];
                    if (!hiddenValues) {
                        return;
                    }

                    fields.push({
                        associatedColumn: fieldKey,
                        alignment: 'center',
                        value: hiddenValues[smilesIndex] ?? '',
                        type: 'text',
                    });
                });

                data.push({
                    id: smilesIndex,
                    fields,
                })
            });

            rsData = {
                ...rsData,
                metricsHeaders,
                modelHeaders,
                moleculeHighlightingHeaders,
                data,
            }

            groupedRsData.push(rsData);
        }
    };

    return {
        groupedRsData,
        predEvalMetadata,
    }
};

export const formatResultsetValidationsData = (predEvals: ResultsetEvaluationMetadata[]) => {
    let evalHeaders: TableHeader[] = [];
    const evalData: TableData[] = [];

    const predEvalWithStats: ResultsetEvaluationMetadataWithStatistics[] = [];

    predEvals.forEach(predEval => {
        const { evaluationMetadata } = predEval;
        if (evaluationMetadata && evaluationMetadata.evaluation_metrics) {
            const evaluation_metrics = evaluationMetadata.evaluation_metrics;
            const statistics = evaluation_metrics.statistics as any[] ?? [];
            if (statistics.length > 0) {
                predEvalWithStats.push({
                    ...predEval,
                    statistics: statistics[0],
                })
            }
        }
    });

    if (predEvalWithStats.length > 0) {
        const statKeys = Object.keys(predEvalWithStats[0].statistics ?? {});
        const classificationKeys = ['acc', 'auc', 'f1score', 'precision', 'recall', 'specificity', 'cohens_kappa', 'mcc', 'tp', 'tn', 'fp', 'fn'];
        const regressionKeys = ['mgd', 'mpd', 'mae', 'r2', 'rmse'];
        const hasClassificationStats = classificationKeys.some(key => statKeys.includes(key));
        const hasRegressionStats = regressionKeys.some(key => statKeys.includes(key));

        // DCM in the current product line is classification-based.
        if (hasClassificationStats || !hasRegressionStats) {
            evalHeaders = classificationHeaders;
        } else {
            evalHeaders = regressionHeaders;
        }

        predEvalWithStats.forEach(predEval => {
            const stats = predEval.statistics;

            let data: TableData = {
                id: predEval.predictionId,
                fields: [],
            };

            const fields: TableDataField[] = [];

            evalHeaders.forEach((header) => {
                let field: TableDataField = {
                    associatedColumn: header.value,
                    alignment: 'center',
                    value: '',
                    type: 'stat',
                };

                if (header.value === 'method') {
                    field.alignment = 'left';
                    field.value = predEval.name;
                    field.type = 'details';
                } else {
                    let statValue = stats[header.value as keyof object] ?? '';
                    if (['tn', 'tp', 'fn', 'fp'].includes(header.value)) {
                        field.type = 'text';
                    };

                    field.value = statValue;
                };

                if (field.value === '') {
                    field.type = 'text';
                }

                fields.push(field);
            });

            if ('cross_val_predict' in stats) {
                fields.push({
                    associatedColumn: 'cross_val_predict',
                    value: stats['cross_val_predict'] ?? [],
                    alignment: 'center',
                    type: 'plot',
                })
            };

            if ('fpr' in stats && 'tpr' in stats) {
                const roc = {
                    name: 'Fold 1',
                    type: 'scatter',
                    x: stats['fpr'],
                    y: stats['tpr'],
                };

                fields.push({
                    associatedColumn: 'roc',
                    value: [roc],
                    alignment: 'center',
                    type: 'plot',
                });
            }

            const yProb = stats['y_prob'] ?? stats['y_pred_proba'];
            const modelOverlayData = model_overlay_data(stats['y_true'], stats['y_pred'], yProb);
            const hasTruthTableStats = ['tp', 'fp', 'fn', 'tn'].every(key => key in stats);
            const truthTableData = hasTruthTableStats
                ? truth_table_data(stats['tp'], stats['fp'], stats['fn'], stats['tn'])
                : undefined;

            if (modelOverlayData) {
                fields.push({
                    associatedColumn: 'overlay',
                    value: modelOverlayData,
                    alignment: 'center',
                    type: 'plot',
                })
            }

            if (truthTableData) {
                fields.push({
                    associatedColumn: 'truthTable',
                    value: truthTableData,
                    alignment: 'center',
                    type: 'plot',
                });
            };

            evalData.push({
                ...data,
                fields,
            });
        })

    };

    return {
        evalHeaders,
        evalData,
    }
}

export const formatDatasetDataframe = (dataframe: { [key: string]: { [key: number]: string | number | null } }) => {
    const dsData: TableData[] = [];
    const dsHeaders: TableHeader[] = [];

    let dfKeys: string[] = [];
    Object.keys(dataframe).forEach(dfKey => {
        if (dfKey === 'structure/SMILES') {
            dfKeys.splice(0, 0, dfKey);
            dfKeys.splice(1, 0, dfKey);
        } else if (dfKey === 'ad-score') {
            dfKeys.splice(2, 0, dfKey);
        } else {
            dfKeys.push(dfKey);
        }
    });

    dfKeys.forEach((key, i) => {
        let header: TableHeader = {
            label: key,
            value: key,
            isShown: true,
            alignment: 'center',
        };

        if (key === 'structure/SMILES') {
            if (i === 0) {
                header.label = 'Structure'; 
                header.alignment = 'left';
            } else {
                header.label = 'SMILES';
            }
        } else if (key === 'ad-score') {
            header.label = 'Applicability Domain';
        }
        
        dsHeaders.push(header);
    });

    const records = Object.keys(Object.values(dataframe)[0]).map(key => Number(key));
    records.forEach(record => {
        const fields: TableDataField[] = [];

        dsHeaders.forEach((header, i) => {
            let field: TableDataField = {
                associatedColumn: header.value,
                value: '',
                type: 'text',
                alignment: 'center',
            };

            const recordValue = dataframe[header.value][record] ?? '';
            field.value = recordValue.toString();

            if (header.value === 'structure/SMILES' && i === 0) {
                field.type = 'structure';
            } else if (header.value === 'ad-score') {
                field.type = 'stat';
            }

            fields.push(field);
        });

        dsData.push({
            id: record,
            fields,
            options: [],
        });
    });

    return {
        dsHeaders,
        dsData,
    }
}

export const formatProtocolStageMetadata = (dataset: Dataset, protocol: Protocol) => {
    const rows = [
        ['Parent Dataset', dataset.name ?? ''],
        ['']
    ]

    if (protocol.protocol_metadata && protocol.protocol_metadata.stage_changes && protocol.protocol_metadata.stage_changes.length > 0) {
        const stage_changes = protocol.protocol_metadata.stage_changes;
        stage_changes.forEach(stageChange => {
            const { function: stageFunction, function_params } = stageChange;
            rows.push(['Stage Function', stageFunction ?? '']);

            if (function_params && Object.entries(function_params).length > 0) {
                rows.push(['Function Params:'])
                for (const [key, value] of Object.entries(function_params)) {
                    rows.push([key, value as any]);
                }
            }
            rows.push([''])
        })
    };

    return rows;
}


export const formatModelData = (models: Model[]) => {
    const modelData: TableData[] = [];
    let modelHeaders: TableHeader[] = regressionHeaders;

    const normalizeStats = (input: unknown, flattenArrays: boolean): { [key: string]: any } => {
        if (!input || typeof input !== 'object') {
            return {};
        }

        let stats = input as { [key: string]: unknown };
        if (Array.isArray(stats)) {
            stats = (stats[0] ?? {}) as { [key: string]: unknown };
        }

        const normalized: { [key: string]: any } = {};
        Object.entries(stats).forEach(([key, value]) => {
            if (Array.isArray(value) && flattenArrays) {
                normalized[key] = value[0] ?? '';
            } else {
                normalized[key] = value ?? '';
            }
        });

        return normalized;
    };

    const getModelStats = (model: Model): { [key: string]: any } => {
        const modelMetadata = (model.model_metadata ?? {}) as any;
        const nestedStats = normalizeStats(modelMetadata.nested_cv_metadata?.output_aggregate_metrics?.statistics, false);
        if (Object.keys(nestedStats).length > 0) {
            return nestedStats;
        }

        const dcmMetadata = modelMetadata.dcm_metadata ?? {};
        const dcmCvStats = normalizeStats(dcmMetadata.submodel_nested_cv_stats?.cv_performance_df_dict?.stats, true);
        const dcmBinaryStats = normalizeStats(dcmMetadata.submodel_nested_cv_stats?.binary?.output_aggregate_metrics?.statistics, false);

        // Preserve DCM summary scalars while still exposing binary arrays needed for plots.
        return {
            ...dcmBinaryStats,
            ...dcmCvStats,
        };
    };

    const getMethodName = (model: Model): string => {
        const modelMetadata = (model.model_metadata ?? {}) as any;
        const nestedMethod = modelMetadata.nested_cv_metadata?.job_metadata?.cv_model?.ml_base_cls?.name;
        const dcmMethod = modelMetadata.dcm_metadata?.job_metadata?.cv_model?.ml_base_cls?.name;
        return nestedMethod ?? dcmMethod ?? model.ml_method ?? (isDcmModel(model) ? 'DomainClassificationModel' : '');
    };

    const representativeModel = models.find(model => {
        const modelMetadata = (model.model_metadata ?? {}) as any;
        return !!modelMetadata.nested_cv_metadata || isDcmModel(model);
    });
    if (representativeModel) {
        const representativeTaskType = getModelTaskType(representativeModel);
        if (representativeTaskType !== 'regression') {
            modelHeaders = classificationHeaders;
        }
    }

    models.forEach(model => {
        const methodName = getMethodName(model);
        const stats = getModelStats(model);

        if (methodName && Object.keys(stats).length > 0) {
            let data: TableData = {
                id: model.file_hash!,
                fields: [],
                options: [],
            };

            const fields: TableDataField[] = [];

            modelHeaders.forEach((header) => {
                let field: TableDataField = {
                    associatedColumn: header.value,
                    alignment: 'center',
                    value: '',
                    type: 'stat',
                };

                if (header.value === 'method') {
                    field.alignment = 'left';
                    field.value = methodName as string;
                    field.type = 'text';
                } else {
                    let statValue = stats[header.value as keyof object] ?? '';
                    if (['tn', 'tp', 'fn', 'fp'].includes(header.value)) {
                        field.type = 'text';
                    };

                    field.value = statValue;
                };

                if (field.value === '') {
                    field.type = 'text';
                }

                fields.push(field);
            });

            if ('cross_val_predict' in stats) {
                fields.push({
                    associatedColumn: 'cross_val_predict',
                    value: stats['cross_val_predict'] ?? [],
                    alignment: 'center',
                    type: 'plot',
                })
            }

            if ('fpr' in stats && 'tpr' in stats) {
                const roc = {
                    name: 'Fold 1',
                    type: 'scatter',
                    x: stats['fpr'],
                    y: stats['tpr'],
                };

                fields.push({
                    associatedColumn: 'roc',
                    value: [roc],
                    alignment: 'center',
                    type: 'plot',
                });
            }

            const yProb = stats['y_prob'] ?? stats['y_pred_proba'];
            const modelOverlayData = model_overlay_data(stats['y_true'], stats['y_pred'], yProb);
            const hasTruthTableStats = ['tp', 'fp', 'fn', 'tn'].every(key => key in stats);
            const truthTableData = hasTruthTableStats
                ? truth_table_data(stats['tp'], stats['fp'], stats['fn'], stats['tn'])
                : undefined;

            if (modelOverlayData) {
                fields.push({
                    associatedColumn: 'overlay',
                    value: modelOverlayData,
                    alignment: 'center',
                    type: 'plot',
                })
            }

            if (truthTableData) {
                fields.push({
                    associatedColumn: 'truthTable',
                    value: truthTableData,
                    alignment: 'center',
                    type: 'plot',
                });
            };

            modelData.push({
                ...data,
                fields,
            });
        };
    });

    return {
        modelData,
        modelHeaders,
    }
};

const calculatePhysChemProps = (rdKit: RDKitModule, smiles: string) => {
    const mol = rdKit.get_mol(smiles);
    if (mol) {
        const descriptors = JSON.parse(mol.get_descriptors());
        return {
            mass: descriptors['exactmw'],
            crippen_c_logP: descriptors['CrippenClogP'],
            h_bond_acceptors: descriptors['NumHBA'],
            h_bond_donors: descriptors['NumHBD'],
        }

    } else {
        return {
            mass: 0,
            crippen_c_logP: 0,
            h_bond_acceptors: 0,
            h_bond_donors: 0,
        }
    }
}

export const formatReadacrossData = (
    data: {[key: string]: { [key: number]: string | number }},
    instance: RDKitModule,
) => {  
    const dataKeys = Object.keys(data);
    const records = Object.keys(data[dataKeys[0]]).length;

    const filteredDataKeys = dataKeys.filter(key => !['chemical_name', 'smiles', 'tanimoto'].includes(key));

    let readAcrossDataToxOnly: ReadAcrossDataToxOnly[] = [];
    let readAcrossDataWithPhys: ReadAcrossDataType[] = [];

    Array.from(Array(records).keys()).forEach(recordNum => {
        let raData: ReadAcrossDataToxOnly = {
            chemical_name: data["chemical_name"] ? data["chemical_name"][recordNum] as string : undefined,
            smiles: data["smiles"][recordNum] as string,
            tanimoto: data["tanimoto"][recordNum] as number,
            toxicology_data: {},
            clf_pred: {},
            clf_proba: {},
            auc: {},
            reg_pred: {},
            pval: {},
            t0: {},
            true_value: {},
            confusion_label: {},
        };


        filteredDataKeys.forEach(key => {
            if (key.endsWith('__clf_pred')) {
                const toxKey = key.split('__clf_pred')[0];
                raData.clf_pred[toxKey] = data[key][recordNum] as number | null;
            } else if (key.endsWith('__clf_proba')) {
                const toxKey = key.split('__clf_proba')[0];
                raData.clf_proba[toxKey] = data[key][recordNum] as number | null;
            } else if (key.endsWith('__auc')) {
                const toxKey = key.split('__auc')[0];
                raData.auc[toxKey] = data[key][recordNum] as number | null;
            } else if (key.endsWith('__reg_pred')) {
                const toxKey = key.split('__reg_pred')[0];
                raData.reg_pred[toxKey] = data[key][recordNum] as number | null;
            } else if (key.endsWith('__pval')) {
                const toxKey = key.split('__pval')[0];
                raData.pval[toxKey] = data[key][recordNum] as number | null;
            } else if (key.endsWith('__t0')) {
                const toxKey = key.split('__t0')[0];
                raData.t0[toxKey] = data[key][recordNum] as number | null;
            } else if (key.endsWith('__true_value')) {
                const toxKey = key.split('__true_value')[0];
                raData.true_value[toxKey] = data[key][recordNum] as number | null;
            } else if (key.endsWith('__confusion_label')) {
                const toxKey = key.split('__confusion_label')[0];
                raData.confusion_label[toxKey] = data[key][recordNum] as string | null;
            } else {
                raData.toxicology_data[key] = data[key][recordNum] as number;
            }
        });

        readAcrossDataToxOnly.push(raData);

    });

    readAcrossDataToxOnly.forEach(ra => {
        const physChem = calculatePhysChemProps(instance, ra.smiles);
        readAcrossDataWithPhys.push({
            ...ra,
            physchem_data: physChem,
        })
    })

    readAcrossDataWithPhys.sort((a,b) => {
        if (a.tanimoto < b.tanimoto) {
            return 1
        } else if (a.tanimoto > b.tanimoto) {
            return -1;
        } else {
            return 0;
        }
    });

    return readAcrossDataWithPhys;
}


export const formatVisualizationData = async (
    visualization: ResultsetVisualizationType,
    showTrainingSetSmiles: boolean,
) => {
    const { 
        graph_data,
        test_keys,
        train_keys
    } = visualization;

    const visData: {
        id: string | number,
        label: string,
        structure_column: string,
        set: 'train' | 'test',
        set_name: string,
        data: ResultsetVisualizationDownloadFormat[],
    }[] = [];

    const testSets: Dataset[] = [];
    const trainingSets: ModelFamily[] = [];

    if (test_keys.length > 0) {
        const promises = test_keys.map(async (hash) => {
            const response = await customInstance({
                url: `dataset/?dataset_hash=${hash}&include_dataset_metadata=false&include_airflow_metadata=false`,
                method: 'GET',
            });
            const dataset = response as unknown as Dataset;
            return testSets.push(dataset);
        });

        try {
            await Promise.all(promises);
        } catch (err:any) {
            console.log(err);
        }
    };

    if (train_keys.length > 0) {
        const promises = train_keys.map(async (id) => {
            const response = await customInstance({
                url: `model_family/?model_family_id=${id}&include_model_family_metadata=false&include_model_stats=false&include_airflow_metadata=false`,
                method: 'GET',
            });
            const modelFamily = response as unknown as ModelFamily;
            return trainingSets.push(modelFamily);
        });

        try {
            await Promise.all(promises);
        } catch (err:any) {
            console.log(err);
        }
    };

    for (const [key, value] of Object.entries(graph_data)) {
        const setData: ResultsetVisualizationDownloadFormat[] = [];
        let id: string | number = '';
        let set_name: string = '';
        let set: 'test' | 'train' = 'test';
        const matchingTestSet = testSets.find(set => set.file_hash === key.toString());
        const matchingTrainingSet = trainingSets.find(set => set.id === Number(key));
        if (matchingTestSet) {
            set_name = matchingTestSet.name ?? '';
            id = matchingTestSet.file_hash!;
        } else if (matchingTrainingSet) {
            set_name = matchingTrainingSet.name ?? '';
            set = 'train';
            id = matchingTrainingSet.id!;
        };

        const {
            structure_column,
            x: xValues,
            y: yValues,
        } = value;

        const smiles = value["structure/SMILES"];

        smiles.forEach((smileString, i) => {
            let setDataValue: ResultsetVisualizationDownloadFormat = {
                structure: set === 'train' && !showTrainingSetSmiles ? undefined : smileString,
                smiles: set === 'train' && !showTrainingSetSmiles ? undefined : smileString,
                x: xValues[i],
                y: yValues[i],
            };

            setData.push(setDataValue);
        });

        visData.push({
            id,
            label: `${set === 'test' ? 'Test' : 'Train'} - ${set_name}`,
            structure_column,
            set,
            set_name,
            data: setData,
        });
    }

    return visData;

}